import {
  type DropTargetDropEvent,
  type DropTargetGetFeedback,
  type DropTargetOptions,
  MenuItem as ComponentMenuItem,
  notify,
  Tooltip,
  useConfirmModal,
  useDropTarget,
} from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { useBootSplashReady } from '@affine/core/bootstrap/boot-splash';
import {
  recordBootEvent,
  setBootBlocker,
} from '@affine/core/bootstrap/boot-trace';
import { AFFINE_LEFT_SIDEBAR_SHORTCUT } from '@affine/core/commands/affine-keybindings';
import {
  useCanOpenWorkspacePlans,
  usePlanGate,
} from '@affine/core/components/affine/feature-gate';
import { useGuard } from '@affine/core/components/guard/use-guard';
import { useWorkspaceCreateDocPermission } from '@affine/core/components/guard/use-workspace-create-doc-permission';
import { useTrashFolderToast } from '@affine/core/components/hooks/affine/use-trash-folder-toast';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import {
  // StreamLain: AppDownloadButton unused
  // AppDownloadButton,
  AppSidebar,
  MenuItem,
  QuickSearchInput,
  SidebarReorderableItem,
  SidebarReorderableList,
  SidebarScrollableContainer,
} from '@affine/core/modules/app-sidebar/views';
import { AuthService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import {
  createDefaultDocCreateConfig,
  type DocCreateConfig,
} from '@affine/core/modules/doc-share';
import {
  FolderApiService,
  FolderMutationOutboxService,
  type FolderPermissions,
  getRememberedFolderPermissions,
  moveFolderToTrashLocalFirst as moveFolderToTrashLocalFirstAction,
  WorkspaceAreaCacheStore,
  type WorkspaceAreaRef,
} from '@affine/core/modules/folder-api';
import {
  createDefaultFolderCreateConfig,
  type FolderCreateConfig,
  FolderShareModal,
} from '@affine/core/modules/folder-share';
import { journalFolderId, JournalService } from '@affine/core/modules/journal';
import {
  NavigationPanelRevealService,
  NavigationPanelService,
  shouldNavigationPanelRevealOpenSidebar,
} from '@affine/core/modules/navigation-panel';
import { NotificationCountService } from '@affine/core/modules/notification';
import {
  AccessibleFolderCacheStore,
  type AccessibleFolderMeta,
  type AccessibleTrashedFolderMeta,
  FolderChildrenLoading,
  FolderStore,
  OrganizeService,
} from '@affine/core/modules/organize';
import {
  ObjectPermissionMutationService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { CMDKQuickSearchService } from '@affine/core/modules/quicksearch/services/cmdk';
import { WorkspaceShareSettingService } from '@affine/core/modules/share-setting';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain';
import { StreamLainAppBaseIcon } from '@affine/core/modules/streamlain/components/app-base-icon';
import { useSemanticDomRegion } from '@affine/core/modules/streamlain/copilot/semantic-dom-region';
import { UiNavigationItem } from '@affine/core/modules/streamlain/copilot/ui-navigation-item';
import {
  FEATURE_KEY_APP_BASES_CREATE,
  FEATURE_KEY_PRIVATE_FOLDERS,
  FEATURE_KEY_TEAMSPACES,
  getWorkspaceMailMissingFeature,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useWorkspaceAppBases } from '@affine/core/modules/streamlain/hooks/use-workspace-app-bases';
import { getMailAppLabel } from '@affine/core/modules/streamlain/mail/mail-app-label';
import { useHiddenMailFolderDocIds } from '@affine/core/modules/streamlain/mail/mail-folder-visibility-filter';
import { useWorkspaceMailAccounts } from '@affine/core/modules/streamlain/mail/use-workspace-mail';
import {
  areaSidebar$,
  isAppDataChangeCancelled,
  useConfirmAppDataMove,
} from '@affine/core/modules/streamlain/native-app';
import { DocDeleteAppUsage } from '@affine/core/modules/streamlain/native-app/doc-delete-app-usage';
import { OutboxDeletionPendingLine } from '@affine/core/modules/streamlain/sidebar/deletion-pending-line';
import { requestWorkspacePaneSidebar } from '@affine/core/modules/streamlain/workspace-pane';
import {
  type Workspace,
  WorkspaceService,
} from '@affine/core/modules/workspace';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { FolderRole, Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import type { Store } from '@blocksuite/affine/store';
import {
  DeleteIcon,
  EmailIcon,
  FolderIcon,
  ImportIcon,
  LockIcon,
  NotificationIcon,
  PageIcon,
  ShareIcon,
  TeamWorkspaceIcon,
} from '@blocksuite/icons/rc';
import {
  LiveData,
  useFramework,
  useLiveData,
  useService,
  useServiceOptional,
  useServices,
} from '@toeverything/infra';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import type {
  HTMLAttributes,
  JSX,
  ReactElement,
  ReactNode,
  Ref,
  RefObject,
  SVGAttributes,
  SVGProps,
} from 'react';
import {
  cloneElement,
  createContext,
  forwardRef,
  Fragment,
  isValidElement,
  lazy,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CollapsibleSection,
  NavigationPanelCollections,
  NavigationPanelMigrationFavorites,
  NavigationPanelOrganize,
  NavigationPanelTags,
} from '../../desktop/components/navigation-panel';
import { NavigationPanelEmptySection } from '../../desktop/components/navigation-panel/layouts/empty-section';
import { NavigationPanelDocNode } from '../../desktop/components/navigation-panel/nodes/doc';
import { NavigationPanelFolderNode } from '../../desktop/components/navigation-panel/nodes/folder';
import { isFolderKnownEmpty } from '../../desktop/components/navigation-panel/nodes/folder/children-state';
import { FolderEmpty } from '../../desktop/components/navigation-panel/nodes/folder/empty';
import { OrganizeCreateMenu } from '../../desktop/components/navigation-panel/sections/organize/create-menu';
import { organizeChildrenDropEffect } from '../../desktop/components/navigation-panel/sections/organize/dnd';
import {
  forEachNavigationPanelDragSelectionItem,
  getNavigationPanelDragSelection,
  NavigationPanelTreeNode,
  type NavigationPanelTreeNodeDropEffect,
  NavigationPanelTreeRoot,
  NavigationPanelTreeSelectionContext,
  type NavigationPanelTreeSelectionItem,
  useNavigationPanelTrashActions,
} from '../../desktop/components/navigation-panel/tree';
import { NavigationPanelTreeNodeInlineRename } from '../../desktop/components/navigation-panel/tree/inline-rename';
import { NavigationTreeSectionOwner } from '../../desktop/components/navigation-panel/tree/navigation';
import { useNavigationTreeEntries } from '../../desktop/components/navigation-panel/tree/navigation-model';
import { canCompleteNavigationPanelReveal } from '../../desktop/components/navigation-panel/tree/rename-intent';
import { rootDropZone } from '../../desktop/components/navigation-panel/tree/root-drop-zone.css';
import { SidebarAreaHeaderActions } from '../../desktop/components/navigation-panel/tree/sidebar-area-header-actions';
import type { SidebarAreaReorderActions } from '../../desktop/components/navigation-panel/tree/sidebar-area-options-menu';
import {
  NavigationPanelSidebarSortModeProvider,
  useNavigationPanelSidebarSortMode,
} from '../../desktop/components/navigation-panel/tree/sidebar-sort-context';
import {
  compareSidebarManualOrderItems,
  sidebarManualOrderKey,
  type SidebarManualOrderPosition,
  useSidebarManualOrder,
} from '../../desktop/components/navigation-panel/tree/use-sidebar-manual-order';
import { buildSidebarCreateActions } from '../../modules/app-sidebar/views/area-actions/create-actions';
import { SIDEBAR_MENU_ITEM_ICON_SIZE } from '../../modules/app-sidebar/views/sidebar-layout';
import { WorkbenchService } from '../../modules/workbench';
import { ViewService } from '../../modules/workbench/services/view';
import { useWorkspaceInfo } from '../hooks/use-workspace-info';
import { NotificationListPopover } from '../notification/popover';
import {
  useSidebarSearchCollapsed,
  useSidebarSearchState,
} from '../sidebar-tree-search';
import { WorkspaceNavigator } from '../workspace-selector';
import type { DocumentsSidebarContentProps } from './documents-sidebar-content';
import { DocumentsSidebarContent } from './documents-sidebar-content';
import { HelpButton } from './help-button';
import {
  areaCreatePlaceholder,
  mailObjectBadgeIcon,
  mailObjectBaseIcon,
  mailObjectIcon,
  sidebarMainSections,
  sidebarMiddleSection,
  sidebarMiddleSectionContent,
  sidebarMobileActiveSectionGroup,
  workspaceAreaSection,
  workspaceAreaSeparatorLine,
  workspaceAreaSeparatorRow,
  workspaceHeaderSummary,
  workspaceHeaderSummaryBadge,
  workspaceHeaderSummaryIcon,
  workspaceHeaderSummaryItem,
  workspaceSharedAreaGroup,
} from './index.css';
import { InviteMembersButton } from './invite-members-button';
import { useLeftSidebarController } from './left-sidebar-controller';
import { createRootAppSidebarResolvedUiStateStorageKey } from './left-sidebar-ui-state';
import {
  mobileNavigationRailOpen$,
  setMobileNavigationRailOpen,
} from './mobile-navigation-rail';
import { NavigationOverlayPaneOpenState } from './navigation-overlay-pane';
import {
  MOBILE_NAVIGATION_RAIL_QUERY,
  TABLET_NAVIGATION_RAIL_QUERY,
  TWO_PANE_OVERLAY_QUERY,
  useNavigationRailViewport,
} from './navigation-rail-viewport';
import { SidebarAudioPlayer } from './sidebar-audio-player';
import { useSidebarCreateTarget } from './sidebar-create-context';
import * as railStyles from './ui-redesign-rail.css';
import { useSidebarAreaHeaderDropTarget } from './use-sidebar-area-header-drop-target';
import { useWorkspaceNavigation } from './use-workspace-navigation';
import {
  useWorkspaceNavigationItems,
  type WorkspaceNavigationItemModel,
} from './use-workspace-navigation-items';
// TutorialsButton moved to Settings → Tutorials tab
import UserInfo from './user-info';
import { WorkspaceConnectivityStatus } from './workspace-connectivity-status';
import { WorkspaceDocumentNavigation } from './workspace-document-navigation';
import {
  formatSidebarBadgeCount,
  SIDEBAR_BADGE_MAX_COUNT,
  SidebarBadge,
  useWorkspaceMailUnreadCount,
} from './workspace-sidebar-badges';

// The sidebar is the app's shell around the view: a read that is cut to size lists it last.
const SHELL_REGION = { place: 'shell' } as const;

const markSidebarPerformanceSignal = (signal: string) => {
  if (
    document.documentElement.dataset.streamlainCanvasPerformanceRecording !==
    'true'
  ) {
    return;
  }
  performance.mark(`streamlain:sidebar:${signal}`);
};

const LazyUpdaterButton = BUILD_CONFIG.isElectron
  ? lazy(() =>
      import('./updater-button').then(m => ({ default: m.UpdaterButton }))
    )
  : null;

const WORKSPACE_HEADER_NOTIFICATION_BADGE_CAPS = [
  SIDEBAR_BADGE_MAX_COUNT,
  99,
  9,
];
const WORKSPACE_HEADER_MAIL_BADGE_CAPS = [99];
const WORKSPACE_HEADER_SUMMARY_ITEM_GAP = 8;
const WORKSPACE_HEADER_SUMMARY_ICON_BADGE_GAP = 4;
const WORKSPACE_HEADER_SUMMARY_BADGE_HORIZONTAL_PADDING = 8;
const WORKSPACE_HEADER_SUMMARY_BADGE_CHARACTER_WIDTH = 7;
const APP_AREA_ROOT_SYSTEM_KIND = 'app_area_root';
const APP_BASE_FOLDER_SYSTEM_KIND = 'app_base_folder';
const TEAMSPACE_ROOT_SYSTEM_KIND = 'teamspace_root';
const TEAM_MAILBOXES_FOLDER_SYSTEM_KIND = 'team_mailboxes_folder';

const findSidebarDocElement = (
  root: HTMLElement,
  docId: string
): HTMLElement | null =>
  Array.from(
    root.querySelectorAll<HTMLElement>('[data-testid^="navigation-panel-doc-"]')
  ).find(
    element => element.dataset.testid === `navigation-panel-doc-${docId}`
  ) ?? null;

const findSidebarFolderElement = (
  root: HTMLElement,
  folderId: string
): HTMLElement | null =>
  Array.from(
    root.querySelectorAll<HTMLElement>(
      '[data-testid^="navigation-panel-folder-"], [data-teamspace-root-id]'
    )
  ).find(
    element =>
      element.dataset.testid === `navigation-panel-folder-${folderId}` ||
      element.dataset.teamspaceRootId === folderId
  ) ?? null;

const isSidebarTargetRootSectionExpanded = (element: HTMLElement) =>
  element.closest<HTMLElement>(
    '[data-testid="sidebar-personal"], [data-testid="sidebar-team-areas"]'
  )?.dataset.expanded !== 'false';

const scrollSidebarElementIntoView = (element: HTMLElement) => {
  const viewport = element.closest<HTMLElement>(
    '[data-sidebar-scroll-viewport="true"]'
  );
  if (!viewport) {
    element.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const viewportRect = viewport.getBoundingClientRect();
  const nextScrollTop = viewport.scrollTop + elementRect.top - viewportRect.top;
  const maxScrollTop = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight
  );

  viewport.scrollTo({
    top: Math.min(maxScrollTop, Math.max(0, nextScrollTop)),
    behavior: 'smooth',
  });
};

function getSidebarFolderPath(
  folder: AccessibleFolderMeta,
  foldersById: Map<string, AccessibleFolderMeta>,
  workspaceRootFolderId: string | null,
  visited = new Set<string>()
): string[] | null {
  if (visited.has(folder.id)) {
    return null;
  }
  visited.add(folder.id);

  if (folder.isWorkspaceRoot || folder.id === workspaceRootFolderId) {
    return ['organize'];
  }
  if (folder.systemKind === 'private_root') {
    return ['private'];
  }
  if (folder.systemKind === APP_AREA_ROOT_SYSTEM_KIND) {
    return ['app-area'];
  }
  if (folder.systemKind === TEAMSPACE_ROOT_SYSTEM_KIND) {
    return ['teamspace', folder.id];
  }

  const parent = folder.parentId ? foldersById.get(folder.parentId) : null;
  const parentPath = parent
    ? getSidebarFolderPath(parent, foldersById, workspaceRootFolderId, visited)
    : folder.parentId === null
      ? ['organize']
      : null;
  return parentPath ? [...parentPath, `folder-${folder.id}`] : null;
}

function getSidebarFolderPathForDoc(
  accessibleFolderCache: AccessibleFolderCacheStore,
  docId: string
): string[] | null {
  const folders = accessibleFolderCache.getCachedFolders();
  const foldersById = new Map(folders.map(folder => [folder.id, folder]));
  const workspaceRootFolderId =
    accessibleFolderCache.getCachedWorkspaceRootFolderId();

  if (
    workspaceRootFolderId &&
    accessibleFolderCache.getCachedRootFolderDocIds().includes(docId)
  ) {
    const rootFolder = foldersById.get(workspaceRootFolderId);
    return rootFolder
      ? getSidebarFolderPath(rootFolder, foldersById, workspaceRootFolderId)
      : ['organize'];
  }

  const containingFolder = folders.find(folder =>
    (folder.docIds ?? []).includes(docId)
  );
  return containingFolder
    ? getSidebarFolderPath(containingFolder, foldersById, workspaceRootFolderId)
    : null;
}

function getSidebarFolderPathForFolder(
  accessibleFolderCache: AccessibleFolderCacheStore,
  folderId: string
): string[] | null {
  const folders = accessibleFolderCache.getCachedFolders();
  const foldersById = new Map(folders.map(folder => [folder.id, folder]));
  const folder = foldersById.get(folderId);
  return folder
    ? getSidebarFolderPath(
        folder,
        foldersById,
        accessibleFolderCache.getCachedWorkspaceRootFolderId()
      )
    : null;
}

function useMaterializeFolderChildren(
  accessibleFolderCache: AccessibleFolderCacheStore,
  folderIds: readonly string[],
  diagnosticReason: string
) {
  useEffect(() => {
    for (const folderId of folderIds) {
      accessibleFolderCache
        .fetchFolderChildren(folderId, { diagnosticReason })
        .catch(() => {});
    }
  }, [accessibleFolderCache, diagnosticReason, folderIds]);
}

function expandSidebarFolderPath(
  navigationPanelService: NavigationPanelService,
  path: string[]
) {
  const startIndex = path[0] === 'teamspace' ? 2 : 1;
  for (let index = startIndex; index <= path.length; index++) {
    navigationPanelService.setCollapsed(path.slice(0, index), false);
  }
}

function getRootSidebarSectionForFolderPath(
  path: string[]
): 'personal' | 'teamAreas' | 'apps' | null {
  if (path[0] === 'private') return 'personal';
  if (path[0] === 'organize' || path[0] === 'teamspace') return 'teamAreas';
  if (path[0] === 'app-area') return 'apps';
  return null;
}

export type RootAppSidebarProps = {
  isPublicWorkspace: boolean;
  onOpenQuickSearchModal: () => void;
  onOpenSettingModal: () => void;
  currentWorkspace: Workspace;
  openPage: (pageId: string) => void;
  createPage: () => Store;
  paths: {
    all: (workspaceId: string) => string;
    trash: (workspaceId: string) => string;
    shared: (workspaceId: string) => string;
  };
};

type WorkspaceAreaCreationContextValue = {
  areas: WorkspaceAreaRef[];
  canCreateArea: boolean;
  createArea: (name?: string) => Promise<void>;
  newAreaRootFolderId: string | null;
  clearNewAreaRootFolderId: () => void;
  refreshAreas: () => void;
};

const WorkspaceAreaCreationContext =
  createContext<WorkspaceAreaCreationContextValue | null>(null);

const useWorkspaceAreaCreation = () => useContext(WorkspaceAreaCreationContext);
const noopRefreshWorkspaceAreas = () => {};
const noopClearNewWorkspaceArea = () => {};

const enqueueFolderCreatePermissionGrants = (
  objectPermissionMutation: ObjectPermissionMutationService,
  folderId: string,
  config: FolderCreateConfig
) =>
  objectPermissionMutation.grantPendingAssignments(
    { id: folderId, kind: 'folder' },
    config
  );

const PrivateFolderIcon = ({ className }: { className?: string }) => (
  <FolderIcon className={className} />
);

const AppBaseFolderIcon = ({ className }: { className?: string }) => (
  <StreamLainAppBaseIcon className={className} />
);

const MailFolderIcon = ({ className }: { className?: string }) => (
  <span className={clsx(mailObjectIcon, className)}>
    <FolderIcon className={mailObjectBaseIcon} />
    <EmailIcon className={mailObjectBadgeIcon} />
  </span>
);

const MailDocIcon = ({ className }: { className?: string }) => (
  <span className={clsx(mailObjectIcon, className)}>
    <PageIcon className={mailObjectBaseIcon} />
    <EmailIcon className={mailObjectBadgeIcon} />
  </span>
);

type PrivateDropPosition =
  | { entityType: 'doc'; at: 'before' | 'after'; entityId: string }
  | { entityType: 'folder'; at: 'before' | 'after'; entityId: string };

type PrivateSidebarChildItem =
  | { key: string; label: string; type: 'doc'; docId: string }
  | {
      key: string;
      label: string;
      type: 'folder';
      folder: AccessibleFolderMeta;
    };

type AppAreaSidebarChildItem =
  | { key: string; label: string; type: 'doc'; docId: string }
  | {
      key: string;
      label: string;
      type: 'folder';
      folder: AccessibleFolderMeta;
    };

type WorkspaceSidebarAreaSectionItem = {
  key: string;
  label: string;
  render: (reorderActions?: SidebarAreaReorderActions) => ReactElement | null;
};

function compareWorkspaceSidebarAreaSections(
  a: WorkspaceSidebarAreaSectionItem,
  b: WorkspaceSidebarAreaSectionItem
): number {
  return compareSidebarManualOrderItems(a, b);
}

const privateFolderDropEffect: NavigationPanelTreeNodeDropEffect = data => {
  const entityType = data.source.data.entity?.type;
  if (entityType !== 'folder') {
    return;
  }
  if (
    data.treeInstruction?.type === 'make-child' ||
    data.treeInstruction?.type === 'reorder-above' ||
    data.treeInstruction?.type === 'reorder-below'
  ) {
    return 'move';
  }
  return;
};

function getPrivateDropPosition(
  data: DropTargetDropEvent<AffineDNDData>,
  entityType: 'doc' | 'folder',
  entityId: string
): PrivateDropPosition | undefined {
  if (
    data.treeInstruction?.type !== 'reorder-above' &&
    data.treeInstruction?.type !== 'reorder-below'
  ) {
    return;
  }

  return {
    entityType,
    entityId,
    at: data.treeInstruction.type === 'reorder-below' ? 'after' : 'before',
  };
}

function toSidebarOrderPosition(
  position: PrivateDropPosition
): SidebarManualOrderPosition {
  return {
    at: position.at,
    key: sidebarManualOrderKey(position.entityType, position.entityId),
  };
}

function canDropPrivateEntity(
  args: DropTargetGetFeedback<AffineDNDData>,
  options: {
    targetFolderId?: string;
    allFolders?: AccessibleFolderMeta[];
    allowMakeChild?: boolean;
    canDropDoc?: (args: DropTargetGetFeedback<AffineDNDData>) => boolean;
  } = {}
): boolean {
  const entity = args.source.data.entity;
  const instructionType = args.treeInstruction?.type;
  if (
    instructionType &&
    instructionType !== 'make-child' &&
    instructionType !== 'reorder-above' &&
    instructionType !== 'reorder-below'
  ) {
    return false;
  }
  if (instructionType === 'make-child' && options.allowMakeChild === false) {
    return false;
  }

  if (entity?.type === 'doc') {
    return options.canDropDoc?.(args) ?? false;
  }
  if (entity?.type !== 'folder') {
    return false;
  }

  if (!options.targetFolderId) {
    return true;
  }
  if (entity.id === options.targetFolderId) {
    return false;
  }

  return !isFolderDescendant(
    options.targetFolderId,
    entity.id,
    options.allFolders ?? []
  );
}

function getCachedDocSourceFolderId(
  accessibleFolderCache: AccessibleFolderCacheStore,
  docId: string
): string | null {
  const cachedFolder = accessibleFolderCache
    .getCachedFolders()
    .find(folder => folder.docIds.includes(docId));
  if (cachedFolder) {
    return cachedFolder.id;
  }

  const rootFolderId = accessibleFolderCache.getCachedWorkspaceRootFolderId();
  if (
    rootFolderId &&
    accessibleFolderCache.getCachedRootFolderDocIds().includes(docId)
  ) {
    return rootFolderId;
  }

  return null;
}

function cachedFolderAllowsDocRemoval(
  workspaceId: string,
  accessibleFolderCache: AccessibleFolderCacheStore,
  docId: string,
  targetFolderId: string
): boolean {
  const sourceFolderId = getCachedDocSourceFolderId(
    accessibleFolderCache,
    docId
  );
  if (!sourceFolderId || sourceFolderId === targetFolderId) {
    return true;
  }

  const sourceFolder = accessibleFolderCache.getCachedFolder(sourceFolderId);
  if (sourceFolder?.systemKind === 'private_mail') {
    return false;
  }

  const sourcePermissions = getRememberedFolderPermissions(
    workspaceId,
    sourceFolderId
  );
  return sourcePermissions?.Folder_RemoveDoc !== false;
}

function cachedFolderAllowsDocAdd(
  workspaceId: string,
  targetFolderId: string,
  targetPermissions?: FolderPermissions | null
): boolean {
  const permissions =
    targetPermissions ??
    getRememberedFolderPermissions(workspaceId, targetFolderId);
  return permissions?.Folder_AddDoc !== false;
}

function createOptimisticPrivateFolderMeta({
  id,
  inheritPermissions = true,
  name,
  parentId,
  ownerUserId = null,
  visibility = 'private',
}: {
  id: string;
  inheritPermissions?: boolean;
  name: string;
  parentId: string;
  ownerUserId?: string | null;
  visibility?: 'public' | 'private';
}): AccessibleFolderMeta {
  return {
    id,
    name,
    parentId,
    ownerUserId,
    systemKind: null,
    visibility,
    inheritPermissions,
    isWorkspaceRoot: false,
    immutable: false,
    storageLimitBytes: null,
    storageUsedBytes: null,
    metadataOnly: false,
    docIds: [],
  };
}

const PrivateSidebarSection = ({
  hideJournalFolder = false,
  onActionsChange,
}: {
  hideJournalFolder?: boolean;
  onActionsChange: (actions: ReactNode) => void;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const { isFeatureAllowed } = useFeatureGate();
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();

  if (workspace.flavour === 'local') {
    return null;
  }
  const hasPrivateFolders = isFeatureAllowed(FEATURE_KEY_PRIVATE_FOLDERS);
  if (!hasPrivateFolders) {
    return null;
  }

  return (
    <PrivateSidebarSectionContent
      hideJournalFolder={hideJournalFolder}
      onActionsChange={onActionsChange}
      showMailFolder={getWorkspaceMailMissingFeature(isFeatureAllowed) === null}
      hiddenMailFolderDocIds={hiddenMailFolderDocIds}
    />
  );
};

const PrivateSidebarSectionContent = ({
  hideJournalFolder,
  onActionsChange,
  showMailFolder,
  hiddenMailFolderDocIds,
}: {
  hideJournalFolder: boolean;
  onActionsChange: (actions: ReactNode) => void;
  showMailFolder: boolean;
  hiddenMailFolderDocIds: ReadonlySet<string>;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;
  const navigationPanelService = useService(NavigationPanelService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const confirmAppDataMove = useConfirmAppDataMove();
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const organizeService = useService(OrganizeService);
  const authService = useService(AuthService);
  const selectionContext = useContext(NavigationPanelTreeSelectionContext);
  const t = useI18n();
  const account = useLiveData(authService.session.account$);
  const workspaceAreaCreation = useWorkspaceAreaCreation();
  const { canCreateDocEffective } = useWorkspaceCreateDocPermission();
  const pageHelper = usePageHelper();
  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const privatePath = useMemo(() => ['private'], []);
  const navigationHeaderRef = useRef<HTMLDivElement | null>(null);
  const journalService = useService(JournalService);
  const journalFolder = useLiveData(
    useMemo(
      () => journalService.journalFolder$(account?.id ?? null),
      [journalService, account?.id]
    )
  );
  const journalDocIds = useLiveData(
    useService(JournalService).allJournalDocIds$
  );
  const journalDocIdSet = useMemo(
    () => new Set(journalDocIds),
    [journalDocIds]
  );
  const expandPersonal = useCallback(
    () => navigationPanelService.setCollapsed(['personal'], false),
    [navigationPanelService]
  );

  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const privateRoot = useMemo(
    () =>
      accessibleFolders.find(folder => folder.systemKind === 'private_root') ??
      null,
    [accessibleFolders]
  );
  const privateRootId = privateRoot?.id ?? null;
  const privateArea = useMemo(
    () =>
      (workspaceAreaCreation?.areas ?? []).find(
        area => area.kind === 'private' && area.rootFolderId === privateRootId
      ) ?? null,
    [privateRootId, workspaceAreaCreation?.areas]
  );
  const canCreatePrivateDocument =
    canCreateDocEffective && privateArea?.permissions.Folder_AddDoc === true;
  const canCreatePrivateFolder =
    privateArea?.permissions.Folder_CreateSubfolder === true;
  const privateRootDocIds = useMemo(
    () => privateRoot?.docIds ?? [],
    [privateRoot?.docIds]
  );
  const mailFolder = privateRootId
    ? (accessibleFolders.find(
        folder =>
          folder.parentId === privateRootId &&
          folder.systemKind === 'private_mail'
      ) ?? null)
    : null;
  const mailFolderIds = useMemo(
    () => (mailFolder ? [mailFolder.id] : []),
    [mailFolder]
  );
  useMaterializeFolderChildren(
    accessibleFolderCache,
    mailFolderIds,
    'root-sidebar:private-mail-folder'
  );
  const visibleMailDocIds = useMemo(
    () =>
      (mailFolder?.docIds ?? []).filter(
        docId => !hiddenMailFolderDocIds.has(docId)
      ),
    [hiddenMailFolderDocIds, mailFolder?.docIds]
  );
  const accessibleDocs$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAllAccessibleDocs(),
        accessibleDocCache.getCachedDocs()
      ),
    [accessibleDocCache]
  );
  const accessibleDocs = useLiveData(accessibleDocs$);
  const docTitleById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.title])),
    [accessibleDocs]
  );
  const docTrashStateById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.trashed])),
    [accessibleDocs]
  );
  const privateRootFolders = useMemo(
    () =>
      privateRootId
        ? accessibleFolders.filter(
            folder =>
              folder.parentId === privateRootId &&
              folder.systemKind !== 'private_mail' &&
              (!hideJournalFolder ||
                (folder.id !== journalFolderId(privateRootId) &&
                  !folder.id.startsWith(`${journalFolderId(privateRootId)}_`) &&
                  folder.id !== journalFolder?.id))
          )
        : [],
    [accessibleFolders, hideJournalFolder, journalFolder?.id, privateRootId]
  );

  useEffect(() => {
    if (!privateRootId) return;
    accessibleFolderCache.fetchFolderChildren(privateRootId).catch(() => {});
  }, [accessibleFolderCache, privateRootId]);

  const privateRootItems = useMemo<PrivateSidebarChildItem[]>(
    () => [
      ...privateRootDocIds
        .filter(
          docId =>
            docTrashStateById.get(docId) !== true &&
            (!hideJournalFolder || !journalDocIdSet.has(docId))
        )
        .map(docId => ({
          key: sidebarManualOrderKey('doc', docId),
          label: docTitleById.get(docId) ?? '',
          type: 'doc' as const,
          docId,
        })),
      ...privateRootFolders.map(folder => ({
        key: sidebarManualOrderKey('folder', folder.id),
        label: folder.name,
        type: 'folder' as const,
        folder,
      })),
    ],
    [
      docTitleById,
      docTrashStateById,
      hideJournalFolder,
      journalDocIdSet,
      privateRootDocIds,
      privateRootFolders,
    ]
  );
  const navigationRoots = useMemo(
    () => [
      ...(showMailFolder && mailFolder && visibleMailDocIds.length
        ? [
            {
              kind: 'folder' as const,
              id: mailFolder.id,
              label: t['com.streamlain.rootAppSidebar.privateFolder.mail'](),
            },
          ]
        : []),
      ...privateRootItems.map(item =>
        item.type === 'folder'
          ? { kind: 'folder' as const, id: item.folder.id, label: item.label }
          : { kind: 'doc' as const, id: item.docId, label: item.label }
      ),
    ],
    [privateRootItems, showMailFolder, mailFolder, visibleMailDocIds.length, t]
  );
  const navigationEntries = useNavigationTreeEntries(
    navigationRoots,
    privatePath,
    hiddenMailFolderDocIds
  );
  const privateRootOrderStorageKey = privateRoot
    ? `streamlain:private-sidebar-manual-order:${workspace.id}:${privateRoot.id}`
    : null;
  const {
    orderedItems: orderedPrivateRootItems,
    placeItem: placePrivateRootItem,
    setSortMode: setPrivateRootSortMode,
    sortMode: privateRootSortMode,
  } = useSidebarManualOrder(
    privateRootOrderStorageKey,
    privateRootItems,
    compareSidebarManualOrderItems
  );

  const handleCreatePrivateDoc = useCallback(
    (config: DocCreateConfig) => {
      if (!privateRoot || !canCreatePrivateDocument) return;
      const newDoc = pageHelper.createPage(undefined, {
        show: false,
        targetFolderId: privateRoot.id,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddDocToFolder(privateRoot.id, newDoc.id);
      if (privateRootSortMode === 'manual') {
        placePrivateRootItem(sidebarManualOrderKey('doc', newDoc.id));
      }
      void objectPermissionMutation
        .grantPendingAssignments({ id: newDoc.id, kind: 'doc' }, config)
        .catch(error => {
          console.error('Failed to apply private document permissions:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
              ](),
          });
        });
      navigationPanelService.setCollapsed(['personal'], false);
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId,
      });
      void accessibleFolderCache.fetchAccessibleFolders();
    },
    [
      accessibleFolderCache,
      canCreatePrivateDocument,
      navigationPanelRevealService,
      navigationPanelService,
      objectPermissionMutation,
      pageHelper,
      placePrivateRootItem,
      privateRoot,
      privateRootSortMode,
      t,
      workspaceId,
    ]
  );

  const handleCreatePrivateFolder = useCallback(
    (config: FolderCreateConfig) => {
      if (!privateRoot || !canCreatePrivateFolder) return;
      const folderId = nanoid();
      const snapshot = accessibleFolderCache.captureOptimisticSnapshot();
      accessibleFolderCache.optimisticUpsertFolder(
        createOptimisticPrivateFolderMeta({
          id: folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          parentId: privateRoot.id,
          ownerUserId: account?.id ?? null,
          visibility: config.visibility,
        }),
        { preserveUntilServerAck: true }
      );
      if (privateRootSortMode === 'manual') {
        placePrivateRootItem(sidebarManualOrderKey('folder', folderId));
      }
      setNewFolderId(folderId);
      navigationPanelService.setCollapsed(['personal'], false);

      void folderMutationOutbox
        .enqueueCreateFolder({
          folderId,
          name: config.name,
          parentId: privateRoot.id,
          visibility: config.visibility,
          inheritPermissions: config.inheritPermissions,
        })
        .then(() =>
          enqueueFolderCreatePermissionGrants(
            objectPermissionMutation,
            folderId,
            config
          ).catch(error => {
            console.error('Failed to apply private folder permissions:', error);
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.folder-permissions-failed'
                ](),
            });
          })
        )
        .catch(error => {
          console.error('Failed to create private folder:', error);
          accessibleFolderCache.restoreOptimisticSnapshot(snapshot);
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.createFailed'](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      canCreatePrivateFolder,
      folderMutationOutbox,
      navigationPanelService,
      objectPermissionMutation,
      placePrivateRootItem,
      privateRoot,
      privateRootSortMode,
      t,
    ]
  );

  const handleCreatePrivateFolderImmediately = useCallback(() => {
    handleCreatePrivateFolder(
      createDefaultFolderCreateConfig(
        'private-folder',
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [handleCreatePrivateFolder, t]);

  const moveEntityToPrivateFolder = useCallback(
    async (
      data: DropTargetDropEvent<AffineDNDData>,
      targetFolderId: string,
      position?: PrivateDropPosition
    ) => {
      const entity = data.source.data.entity;
      if (!entity) {
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();

      if (entity.type === 'doc') {
        if (
          !cachedFolderAllowsDocAdd(workspaceId, targetFolderId) ||
          !cachedFolderAllowsDocRemoval(
            workspaceId,
            accessibleFolderCache,
            entity.id,
            targetFolderId
          )
        ) {
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
              ](),
          });
          return;
        }

        accessibleFolderCache.optimisticAddDocToFolder(
          targetFolderId,
          entity.id,
          position?.entityType === 'doc'
            ? {
                at: position.at,
                docId: position.entityId,
              }
            : undefined
        );
        if (targetFolderId === privateRoot?.id && position) {
          placePrivateRootItem(
            sidebarManualOrderKey('doc', entity.id),
            toSidebarOrderPosition(position)
          );
        }
        navigationPanelService.setCollapsed(['personal'], false);

        void confirmAppDataMove({
          id: entity.id,
          kind: 'doc',
          targetFolderId,
        })
          .then(() =>
            folderMutationOutbox.enqueueAddDocToFolder(
              targetFolderId,
              entity.id
            )
          )
          .catch(error => {
            accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
            if (isAppDataChangeCancelled(error)) return;
            console.error('Failed to move doc to private folder:', error);
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
                ](),
            });
          });
        return;
      }

      if (entity.type === 'folder') {
        if (entity.id === targetFolderId) {
          return;
        }
        const existing = accessibleFolderCache.getCachedFolder(entity.id);
        const yjsNode = organizeService.folderTree.folderNode$(entity.id).value;
        const folderName =
          existing?.name ?? yjsNode?.name$.value ?? t['Folder']();
        if (!existing) {
          accessibleFolderCache.optimisticUpsertFolder(
            createOptimisticPrivateFolderMeta({
              id: entity.id,
              name: folderName,
              parentId: targetFolderId,
              ownerUserId: account?.id ?? null,
            })
          );
        }
        accessibleFolderCache.optimisticMoveFolder(
          entity.id,
          targetFolderId,
          {
            inheritPermissions: existing?.inheritPermissions ?? true,
            name: folderName,
            systemKind: null,
            visibility: 'private',
          },
          position?.entityType === 'folder'
            ? {
                at: position.at,
                folderId: position.entityId,
              }
            : undefined
        );
        if (targetFolderId === privateRoot?.id && position) {
          placePrivateRootItem(
            sidebarManualOrderKey('folder', entity.id),
            toSidebarOrderPosition(position)
          );
        }
        navigationPanelService.setCollapsed(['personal'], false);

        void confirmAppDataMove({
          id: entity.id,
          kind: 'folder',
          targetFolderId,
        })
          .then(() =>
            folderMutationOutbox.enqueueMoveFolder(entity.id, targetFolderId)
          )
          .catch(error => {
            accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
            if (isAppDataChangeCancelled(error)) return;
            console.error('Failed to move item to private folder:', error);
            notify.error({
              title:
                t['com.streamlain.rootAppSidebar.privateFolder.moveFailed'](),
            });
          });
      }
    },
    [
      accessibleFolderCache,
      account?.id,
      confirmAppDataMove,
      folderMutationOutbox,
      navigationPanelService,
      organizeService,
      placePrivateRootItem,
      privateRoot,
      t,
      workspaceId,
    ]
  );

  const handlePrivateRootDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      if (!privateRoot) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          moveEntityToPrivateFolder(itemData, privateRoot.id).catch(error => {
            console.error('Failed to move item to private root:', error);
          });
        });
        selectionContext?.clearSelection();
        return;
      }
      moveEntityToPrivateFolder(data, privateRoot.id).catch(error => {
        console.error('Failed to move item to private root:', error);
      });
    },
    [moveEntityToPrivateFolder, privateRoot, selectionContext]
  );

  const handlePrivateRootChildDrop = useCallback(
    (
      data: DropTargetDropEvent<AffineDNDData>,
      position: PrivateDropPosition
    ) => {
      if (!privateRoot) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          moveEntityToPrivateFolder(itemData, privateRoot.id, position).catch(
            error => {
              console.error('Failed to move item inside private root:', error);
            }
          );
        });
        selectionContext?.clearSelection();
        return;
      }
      moveEntityToPrivateFolder(data, privateRoot.id, position).catch(error => {
        console.error('Failed to move item inside private root:', error);
      });
    },
    [moveEntityToPrivateFolder, privateRoot, selectionContext]
  );

  const privateRootCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      if (!privateRoot) {
        return false;
      }
      if (
        privateRootSortMode !== 'manual' &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      return canDropPrivateEntity(args, {
        canDropDoc: docArgs => {
          const entity = docArgs.source.data.entity;
          return (
            entity?.type === 'doc' &&
            cachedFolderAllowsDocAdd(workspaceId, privateRoot.id) &&
            cachedFolderAllowsDocRemoval(
              workspaceId,
              accessibleFolderCache,
              entity.id,
              privateRoot.id
            )
          );
        },
      });
    },
    [accessibleFolderCache, privateRoot, privateRootSortMode, workspaceId]
  );
  const canDropOnPrivateArea =
    privateRootSortMode !== 'manual' || orderedPrivateRootItems.length === 0;

  const { dropTargetRef: privateRootEmptyDropTargetRef } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: { at: 'navigation-panel:organize:root' },
        onDrop: handlePrivateRootDrop,
        canDrop: privateRootCanDrop,
        allowExternal: true,
      }),
      [handlePrivateRootDrop, privateRootCanDrop]
    );
  const { dropTargetRef: privateRootContentDropTargetRef } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: { at: 'navigation-panel:organize:root' },
        onDrop: handlePrivateRootDrop,
        canDrop: args =>
          canDropOnPrivateArea &&
          ((typeof privateRootCanDrop === 'function'
            ? privateRootCanDrop(args)
            : privateRootCanDrop) ??
            false),
        allowExternal: true,
      }),
      [canDropOnPrivateArea, handlePrivateRootDrop, privateRootCanDrop]
    );

  const personalCreateTarget = useMemo(
    () => ({
      id: 'personal',
      section: 'personal' as const,
      label: t['com.streamlain.rootAppSidebar.personal'](),
      document:
        privateRoot && canCreatePrivateDocument
          ? (name: string) =>
              handleCreatePrivateDoc(
                createDefaultDocCreateConfig(name || t['Untitled']())
              )
          : undefined,
      folder:
        privateRoot && canCreatePrivateFolder
          ? (name: string) =>
              handleCreatePrivateFolder(
                createDefaultFolderCreateConfig(
                  'private-folder',
                  name ||
                    t['com.streamlain.rootAppSidebar.organize.create.folder']()
                )
              )
          : undefined,
    }),
    [
      privateRoot,
      canCreatePrivateDocument,
      canCreatePrivateFolder,
      handleCreatePrivateDoc,
      handleCreatePrivateFolder,
      t,
    ]
  );
  useSidebarCreateTarget(personalCreateTarget);
  const personalActions = useMemo(
    () =>
      privateRoot ? (
        <SidebarAreaHeaderActions
          options={{
            sortMode: privateRootSortMode,
            onSortModeChange: setPrivateRootSortMode,
            testId: 'navigation-panel-private-options-button',
          }}
        />
      ) : null,
    [privateRoot, privateRootSortMode, setPrivateRootSortMode]
  );
  useLayoutEffect(() => {
    onActionsChange(personalActions);
    return () => onActionsChange(null);
  }, [onActionsChange, personalActions]);

  return (
    <NavigationTreeSectionOwner
      path={privatePath}
      label={t['com.streamlain.rootAppSidebar.personal']()}
      entries={navigationEntries}
      headerRef={navigationHeaderRef}
      expanded
      onExpand={expandPersonal}
    >
      <div
        ref={privateRootContentDropTargetRef}
        className={rootDropZone}
        data-testid="navigation-panel-private-root-content"
        data-private-root-id={privateRootId ?? ''}
        data-private-root-item-count={orderedPrivateRootItems.length}
        data-accessible-folder-count={accessibleFolders.length}
      >
        <NavigationPanelSidebarSortModeProvider mode={privateRootSortMode}>
          <NavigationPanelTreeRoot
            placeholder={
              privateRoot &&
              (!hideJournalFolder || journalDocIds.length === 0) ? (
                <SidebarCreatePlaceholder
                  ref={privateRootEmptyDropTargetRef}
                  icon={<PrivateFolderIcon />}
                  message={t[
                    'com.streamlain.rootAppSidebar.organize.empty-folder'
                  ]()}
                  messageTestId="slider-bar-private-empty-message"
                  actionText={
                    canCreatePrivateFolder
                      ? t[
                          'com.streamlain.rootAppSidebar.organize.empty.new-folders-button'
                        ]()
                      : undefined
                  }
                  onActionClick={
                    canCreatePrivateFolder
                      ? handleCreatePrivateFolderImmediately
                      : undefined
                  }
                />
              ) : null
            }
          >
            {showMailFolder && mailFolder && visibleMailDocIds.length ? (
              <MailFolderNode
                folder={mailFolder}
                docIds={visibleMailDocIds}
                docTitleById={docTitleById}
                parentPath={['private']}
              />
            ) : null}
            {orderedPrivateRootItems.map(item =>
              item.type === 'doc' ? (
                <NavigationPanelDocNode
                  key={item.key}
                  docId={item.docId}
                  hideLinkedChildren
                  location={{
                    at: 'navigation-panel:private:root-doc',
                    nodeId: item.docId,
                  }}
                  canDrop={privateRootCanDrop}
                  onDrop={data => {
                    const position = getPrivateDropPosition(
                      data,
                      'doc',
                      item.docId
                    );
                    if (!position) {
                      return;
                    }
                    handlePrivateRootChildDrop(data, position);
                  }}
                  dropEffect={privateFolderDropEffect}
                  parentPath={['private']}
                />
              ) : (
                <PrivateUserFolderNode
                  key={item.key}
                  folder={item.folder}
                  allFolders={accessibleFolders}
                  newFolderId={newFolderId}
                  setNewFolderId={setNewFolderId}
                  parentPath={['private']}
                  allowSiblingDrop={privateRootSortMode === 'manual'}
                  onMoveToFolder={moveEntityToPrivateFolder}
                />
              )
            )}
          </NavigationPanelTreeRoot>
        </NavigationPanelSidebarSortModeProvider>
      </div>
    </NavigationTreeSectionOwner>
  );
};

type SidebarHeaderBadgeAction = {
  key: string;
  count: number;
  icon: ReactElement<SVGAttributes<SVGElement>>;
  label: string;
  testId: string;
  badgeCaps?: readonly number[];
  onClick?: () => void;
  wrapButton?: (button: ReactElement) => ReactNode;
};

const resolveSidebarHeaderBadgeLabels = ({
  actions,
  availableWidth,
}: {
  actions: SidebarHeaderBadgeAction[];
  availableWidth: number | null;
}) => {
  const visibleActions = actions.filter(action => action.count > 0);
  const width = availableWidth ?? Number.POSITIVE_INFINITY;

  if (visibleActions.length === 0) {
    return {};
  }

  const candidates = visibleActions.reduce<
    Array<{ labels: Record<string, string>; rank: number; width: number }>
  >(
    (partialCandidates, action, actionIndex) =>
      partialCandidates.flatMap(candidate =>
        (action.badgeCaps ?? [SIDEBAR_BADGE_MAX_COUNT]).map((cap, capIndex) => {
          const label = formatSidebarBadgeCount(action.count, cap);
          const labelWidth =
            (actionIndex === 0 ? 0 : WORKSPACE_HEADER_SUMMARY_ITEM_GAP) +
            SIDEBAR_MENU_ITEM_ICON_SIZE +
            WORKSPACE_HEADER_SUMMARY_ICON_BADGE_GAP +
            WORKSPACE_HEADER_SUMMARY_BADGE_HORIZONTAL_PADDING +
            label.length * WORKSPACE_HEADER_SUMMARY_BADGE_CHARACTER_WIDTH;

          return {
            labels: {
              ...candidate.labels,
              [action.key]: label,
            },
            rank: candidate.rank + capIndex,
            width: candidate.width + labelWidth,
          };
        })
      ),
    [{ labels: {}, rank: 0, width: 0 }]
  );

  return (
    candidates
      .filter(candidate => candidate.width <= width)
      .sort((a, b) => a.rank - b.rank || b.width - a.width)[0]?.labels ??
    candidates[candidates.length - 1].labels
  );
};

const SidebarHeaderBadgeSummary = ({
  actions,
}: {
  actions: SidebarHeaderBadgeAction[];
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const availableWidth = useElementWidth(ref);
  const visibleActions = useMemo(
    () => actions.filter(action => action.count > 0),
    [actions]
  );
  const labels = useMemo(
    () =>
      resolveSidebarHeaderBadgeLabels({
        actions: visibleActions,
        availableWidth,
      }),
    [availableWidth, visibleActions]
  );

  if (visibleActions.length === 0) return null;

  return (
    <div ref={ref} className={workspaceHeaderSummary}>
      {visibleActions.map(action => {
        const icon = cloneElement(action.icon, {
          className: clsx([
            workspaceHeaderSummaryIcon,
            action.icon.props.className,
          ]),
        });
        const button = (
          <button
            type="button"
            aria-label={action.label}
            className={workspaceHeaderSummaryItem}
            data-testid={action.testId}
            title={action.label}
            onClick={action.onClick}
          >
            {icon}
            <SidebarBadge
              className={workspaceHeaderSummaryBadge}
              count={action.count}
              label={labels[action.key]}
            />
          </button>
        );
        return (
          <Fragment key={action.key}>
            {action.wrapButton ? action.wrapButton(button) : button}
          </Fragment>
        );
      })}
    </div>
  );
};

const useElementWidth = (ref: RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateWidth = () => {
      setWidth(element.getBoundingClientRect().width);
    };
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return width;
};

const toLocalTrashedFolderSummary = (folder: {
  id: string;
  docIds: string[];
}): Pick<AccessibleTrashedFolderMeta, 'id' | 'docIds'> => ({
  id: folder.id,
  docIds: folder.docIds,
});

const useSidebarTrashCount = () => {
  const workspace = useService(WorkspaceService).workspace;
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const folderApiService = useService(FolderApiService);
  const folderStore = useService(FolderStore);
  const isCloudWorkspace = workspace.flavour !== 'local';
  const trashedDocIds = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleDocCache.watchTrashedDocIds(),
          accessibleDocCache
            .getCachedDocs()
            .filter(doc => doc.trashed)
            .map(doc => doc.id)
        ),
      [accessibleDocCache]
    )
  );
  useEffect(() => {
    markSidebarPerformanceSignal('source:trashed-doc-ids');
  }, [trashedDocIds]);
  const [trashedFolders, setTrashedFolders] = useState<
    Pick<AccessibleTrashedFolderMeta, 'id' | 'docIds'>[]
  >([]);

  const refreshTrashedFolders = useCallback(() => {
    if (!isCloudWorkspace) return;
    void accessibleFolderCache
      .fetchTrashedFolders({ force: true })
      .catch(() => {
        // Keep the persisted local-first trash shape while offline.
      });
  }, [accessibleFolderCache, isCloudWorkspace]);

  useEffect(() => {
    if (isCloudWorkspace) {
      const subscription = accessibleFolderCache
        .watchTrashedFolderMetadata$()
        .subscribe(setTrashedFolders);
      refreshTrashedFolders();
      return () => subscription.unsubscribe();
    }
    const subscription = folderStore
      .watchTrashedFolders$()
      .subscribe(folders => {
        setTrashedFolders(folders.map(toLocalTrashedFolderSummary));
      });
    return () => subscription.unsubscribe();
  }, [
    accessibleFolderCache,
    folderStore,
    isCloudWorkspace,
    refreshTrashedFolders,
  ]);

  useEffect(() => {
    const subscription = folderApiService.folderTrashChanged$.subscribe(() => {
      refreshTrashedFolders();
    });
    return () => subscription.unsubscribe();
  }, [folderApiService, refreshTrashedFolders]);

  return useMemo(() => {
    const folderDocIds = new Set(trashedFolders.flatMap(f => f.docIds));
    const trashedDocCount = trashedDocIds.filter(
      docId => !folderDocIds.has(docId)
    ).length;
    return trashedFolders.length + trashedDocCount;
  }, [trashedDocIds, trashedFolders]);
};

const WorkspaceSectionHeaderSummary = ({
  mailCount,
  notificationCount,
}: {
  mailCount: number;
  notificationCount: number;
}) => {
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const t = useI18n();
  const notificationLabel = t['com.streamlain.rootAppSidebar.notifications']();
  const mailLabel = getMailAppLabel(t);
  const handleOpenMail = useCallback(() => {
    workbench.open('/mail');
  }, [workbench]);
  const actions = useMemo<SidebarHeaderBadgeAction[]>(
    () => [
      {
        key: 'notifications',
        count: notificationCount,
        icon: <NotificationIcon />,
        label: notificationLabel,
        testId: 'workspace-header-notifications-button',
        badgeCaps: WORKSPACE_HEADER_NOTIFICATION_BADGE_CAPS,
        wrapButton: button => (
          <NotificationListPopover>
            {() => button as ReactElement<{ ref?: Ref<HTMLElement> }>}
          </NotificationListPopover>
        ),
      },
      {
        key: 'mail',
        count: mailCount,
        icon: <EmailIcon />,
        label: mailLabel,
        testId: 'workspace-header-mail-button',
        badgeCaps: WORKSPACE_HEADER_MAIL_BADGE_CAPS,
        onClick: handleOpenMail,
      },
    ],
    [handleOpenMail, mailCount, mailLabel, notificationCount, notificationLabel]
  );

  return <SidebarHeaderBadgeSummary actions={actions} />;
};

const MailFolderNode = ({
  folder,
  docIds,
  docTitleById,
  parentPath,
  label,
  locked = true,
  testId = 'sidebar-private-mail-folder',
}: {
  folder: AccessibleFolderMeta;
  docIds: readonly string[];
  docTitleById: ReadonlyMap<string, string>;
  parentPath: string[];
  label?: string;
  locked?: boolean;
  testId?: string;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const mailAccounts = useWorkspaceMailAccounts(
    workspace.flavour === 'local' ? null : workspace.id
  );
  const accountLabelByDocId = useMemo(
    () =>
      new Map(
        (mailAccounts.data ?? []).map(account => [
          account.accessDocId,
          account.emailAddress,
        ])
      ),
    [mailAccounts.data]
  );
  const navigationPanelService = useService(NavigationPanelService);
  const path = useMemo(
    () => [...parentPath, `folder-${folder.id}`],
    [folder.id, parentPath]
  );
  const collapsed = useSidebarSearchCollapsed(
    useLiveData(navigationPanelService.collapsed$(path)),
    path
  );
  const setCollapsed = useCallback(
    (value: boolean) => navigationPanelService.setCollapsed(path, value),
    [navigationPanelService, path]
  );

  return (
    <NavigationPanelTreeNode
      icon={MailFolderIcon}
      name={label ?? t['com.streamlain.rootAppSidebar.privateFolder.mail']()}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      postfix={locked ? <LockIcon /> : undefined}
      data-testid={testId}
      explorerIconConfig={{
        where: 'folder',
        id: folder.id,
      }}
      reorderable={false}
      canDrop={false}
    >
      {docIds.map(docId => (
        <MailDocTreeNode
          key={docId}
          docId={docId}
          name={
            accountLabelByDocId.get(docId) ??
            docTitleById.get(docId) ??
            undefined
          }
          locked={locked}
        />
      ))}
    </NavigationPanelTreeNode>
  );
};

const MailDocTreeNode = ({
  docId,
  locked = true,
  name,
}: {
  docId: string;
  locked?: boolean;
  name?: string;
}) => {
  const { workbenchService } = useServices({ WorkbenchService });
  const t = useI18n();
  const workbench = workbenchService.workbench;
  const personalDocActive = useLiveData(
    workbench.location$.selector(location => location.pathname === `/${docId}`)
  );
  const openPersonalDoc = useCallback(() => {
    workbench.openDoc(docId, { at: 'active' });
  }, [docId, workbench]);

  return (
    <NavigationPanelTreeNode
      icon={MailDocIcon}
      navigationObject={{ kind: 'doc', id: docId }}
      name={name || t['com.streamlain.rootAppSidebar.privateFolder.mailDoc']()}
      active={personalDocActive}
      disabled={false}
      onClick={openPersonalDoc}
      to={`/${docId}`}
      collapsed
      setCollapsed={() => {}}
      collapsible={false}
      postfix={locked ? <LockIcon /> : undefined}
      reorderable={false}
      canDrop={false}
      data-testid={locked ? 'sidebar-mail-personal-doc' : 'sidebar-mail-doc'}
    />
  );
};

function asSidebarRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function getConfigStringField(config: unknown, field: string): string | null {
  const value = asSidebarRecord(config)?.[field];
  return typeof value === 'string' && value.trim() ? value : null;
}

function useAppAreaSidebarAvailability() {
  const workspace = useService(WorkspaceService).workspace;
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const { isFeatureAllowed } = useFeatureGate();
  const shareSetting = useService(WorkspaceShareSettingService).sharePreview;
  const appBasesEnabled = useLiveData(shareSetting.appBasesEnabled$);
  const hasAppBases = isFeatureAllowed(FEATURE_KEY_APP_BASES_CREATE);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const appAreaRoot = useMemo(
    () =>
      accessibleFolders.find(
        folder =>
          folder.systemKind === APP_AREA_ROOT_SYSTEM_KIND &&
          folder.parentId === null
      ) ?? null,
    [accessibleFolders]
  );
  const canLoadAppArea =
    workspace.flavour !== 'local' && hasAppBases && appBasesEnabled === true;

  return {
    accessibleFolders,
    appAreaRoot,
    canLoadAppArea,
  };
}

const AppAreaSidebarContent = ({
  onActionsChange,
}: {
  onActionsChange: (actions: ReactNode) => void;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const { trashAppBase } = useNavigationPanelTrashActions();
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const canViewAppBases = useGuard('Workspace_AppBases_View') === true;
  const { accessibleFolders, appAreaRoot, canLoadAppArea } =
    useAppAreaSidebarAvailability();
  const accessibleDocs = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleDocCache.watchAllAccessibleDocs(),
          accessibleDocCache.getCachedDocs()
        ),
      [accessibleDocCache]
    )
  );

  const { views, isValidating: appBasesValidating } = useWorkspaceAppBases(
    canLoadAppArea && (canViewAppBases || appAreaRoot) ? workspace.id : null
  );
  const appAreaRootId = appAreaRoot?.id ?? null;

  useEffect(() => {
    if (!canLoadAppArea) {
      return;
    }
    accessibleFolderCache.fetchAccessibleFolders().catch(error => {
      console.error('Failed to load app area folders:', error);
    });
    accessibleDocCache.fetchAccessibleDocs().catch(error => {
      console.error('Failed to load app area docs:', error);
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    canLoadAppArea,
    appBasesValidating,
    views?.length,
  ]);
  useEffect(() => {
    if (!canLoadAppArea || !appAreaRootId) {
      return;
    }
    accessibleFolderCache
      .fetchFolderChildren(appAreaRootId, {
        diagnosticReason: 'app-area-root',
        force: true,
        reuseRecent: true,
      })
      .catch(error => {
        console.error('Failed to load app folders:', error);
      });
  }, [
    accessibleFolderCache,
    appAreaRootId,
    canLoadAppArea,
    appBasesValidating,
    views?.length,
  ]);
  const appByFolderId = useMemo(() => {
    const next = new Map<string, { id: string }>();
    for (const view of views ?? []) {
      const appFolderId = getConfigStringField(view.config, 'appFolderId');
      if (appFolderId) {
        next.set(appFolderId, { id: view.id });
      }
    }
    return next;
  }, [views]);
  const appRootFolders = useMemo(
    () =>
      appAreaRoot
        ? accessibleFolders
            .filter(
              folder =>
                folder.parentId === appAreaRoot.id &&
                folder.systemKind === APP_BASE_FOLDER_SYSTEM_KIND
            )
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [accessibleFolders, appAreaRoot]
  );

  const appItems = useMemo(
    () =>
      appRootFolders.map(folder => ({
        key: folder.id,
        label: folder.name,
        type: 'folder',
        folder,
      })),
    [appRootFolders]
  );
  const {
    orderedItems: orderedApps,
    sortMode,
    setSortMode,
  } = useSidebarManualOrder(
    `streamlain:app-area-section-order:${workspace.id}`,
    appItems,
    compareSidebarManualOrderItems,
    { defaultSortMode: 'alphabetical' }
  );
  const appActions = useMemo(
    () => (
      <SidebarAreaHeaderActions
        options={{
          sortMode,
          onSortModeChange: setSortMode,
          testId: 'navigation-panel-apps-options-button',
        }}
      />
    ),
    [sortMode, setSortMode]
  );
  useLayoutEffect(() => {
    onActionsChange(appActions);
    return () => onActionsChange(null);
  }, [onActionsChange, appActions]);

  if (!canLoadAppArea || !appAreaRoot) {
    return null;
  }

  return (
    <NavigationPanelTreeRoot
      placeholder={
        appRootFolders.length === 0 ? (
          <NavigationPanelEmptySection
            icon={<StreamLainAppBaseIcon />}
            message={t['com.streamlain.rootAppSidebar.appArea.empty']()}
          />
        ) : null
      }
    >
      {orderedApps.map(({ folder }) => (
        <AppAreaFolderNode
          key={folder.id}
          folder={folder}
          allFolders={accessibleFolders}
          accessibleDocs={accessibleDocs}
          parentPath={['app-area']}
          appBaseId={appByFolderId.get(folder.id)?.id ?? null}
          onTrashAppBase={trashAppBase}
        />
      ))}
    </NavigationPanelTreeRoot>
  );
};

const AppAreaFolderNode = ({
  folder,
  allFolders,
  accessibleDocs,
  parentPath,
  appBaseId,
  onTrashAppBase,
}: {
  folder: AccessibleFolderMeta;
  allFolders: AccessibleFolderMeta[];
  accessibleDocs: Array<{ id: string; title: string; trashed: boolean }>;
  parentPath: string[];
  appBaseId: string | null;
  onTrashAppBase: (appBaseId: string) => Promise<boolean>;
}) => {
  const t = useI18n();
  const navigationPanelService = useService(NavigationPanelService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const { openConfirmModal } = useConfirmModal();
  const framework = useFramework();
  const path = useMemo(
    () => [...parentPath, `folder-${folder.id}`],
    [folder.id, parentPath]
  );
  const collapsed = useSidebarSearchCollapsed(
    useLiveData(navigationPanelService.collapsed$(path)),
    path
  );
  const setCollapsed = useCallback(
    (value: boolean) => navigationPanelService.setCollapsed(path, value),
    [navigationPanelService, path]
  );

  useEffect(() => {
    void accessibleFolderCache
      .fetchFolderChildren(folder.id, {
        diagnosticReason: 'app-area-folder',
        force: true,
        reuseRecent: true,
      })
      .catch(() => {});
  }, [accessibleFolderCache, folder.id]);
  const docTitleById = useMemo(
    () =>
      new Map(
        accessibleDocs
          .filter(doc => !doc.trashed)
          .map(doc => [doc.id, doc.title])
      ),
    [accessibleDocs]
  );
  const childFolders = useMemo(
    () =>
      allFolders
        .filter(child => child.parentId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allFolders, folder.id]
  );
  const childItems = useMemo<AppAreaSidebarChildItem[]>(
    () =>
      [
        ...childFolders.map(child => ({
          key: sidebarManualOrderKey('folder', child.id),
          label: child.name,
          type: 'folder' as const,
          folder: child,
        })),
        ...(folder.docIds ?? [])
          .filter(docId => docTitleById.has(docId))
          .map(docId => ({
            key: sidebarManualOrderKey('doc', docId),
            label: docTitleById.get(docId) ?? '',
            type: 'doc' as const,
            docId,
          })),
      ].sort(compareSidebarManualOrderItems),
    [childFolders, docTitleById, folder.docIds]
  );
  const operations = useMemo(
    () => [
      {
        index: 200,
        view: (
          <ComponentMenuItem
            data-testid="app-folder-menu-configure"
            prefixIcon={<ShareIcon />}
            onClick={() => setShareModalOpen(true)}
          >
            {t['com.streamlain.share-menu.shareButton']()}
          </ComponentMenuItem>
        ),
      },
      ...(appBaseId
        ? [
            {
              index: 9999,
              view: (
                <ComponentMenuItem
                  data-testid="app-folder-menu-trash"
                  data-ui-delete="app"
                  prefixIcon={<DeleteIcon />}
                  type="danger"
                  onClick={() =>
                    openConfirmModal({
                      title: t['com.streamlain.moveToTrash.title'](),
                      description: t[
                        'com.streamlain.moveToTrash.confirmModal.description'
                      ]({ title: folder.name }),
                      children: (
                        <DocDeleteAppUsage
                          framework={framework}
                          target={{ kind: 'folder', ids: [folder.id] }}
                          operation="trash"
                        />
                      ),
                      cancelText: t['Cancel'](),
                      confirmText: t['com.streamlain.moveToTrash.title'](),
                      confirmButtonOptions: { variant: 'error' },
                      onConfirm: () => void onTrashAppBase(appBaseId),
                    })
                  }
                >
                  {t['com.streamlain.moveToTrash.title']()}
                </ComponentMenuItem>
              ),
            },
          ]
        : []),
    ],
    [
      appBaseId,
      folder.id,
      folder.name,
      framework,
      onTrashAppBase,
      openConfirmModal,
      t,
    ]
  );
  const selectionItem = useMemo<NavigationPanelTreeSelectionItem | undefined>(
    () =>
      appBaseId
        ? {
            appBaseTrash: { appBaseId },
            entity: { type: 'folder', id: folder.id },
            folderTrash: { canTrashFolder: false },
            from: {
              at: 'navigation-panel:apps:folder-node',
              nodeId: folder.id,
            },
            label: folder.name,
          }
        : undefined,
    [appBaseId, folder.id, folder.name]
  );

  return (
    <>
      <NavigationPanelTreeNode
        icon={AppBaseFolderIcon}
        name={folder.name}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        operations={operations}
        selectionItem={selectionItem}
        revealOperations
        reorderable={false}
        canDrop={false}
        childrenEmpty={isFolderKnownEmpty(
          folder.hasChildren,
          childItems.length
        )}
        childrenPlaceholder={
          folder.hasChildren === true ? undefined : <FolderEmpty />
        }
        data-testid={`navigation-panel-app-folder-${folder.id}`}
        explorerIconConfig={{
          where: 'folder',
          id: folder.id,
        }}
      >
        {childItems.map(item =>
          item.type === 'doc' ? (
            <NavigationPanelDocNode
              key={item.key}
              docId={item.docId}
              icon={PageIcon}
              hideLinkedChildren
              hideTrashOperation
              trashOperationUnavailableReason={t[
                'com.streamlain.rootAppSidebar.appArea.docManagedHint'
              ]()}
              disableDrag
              reorderable={false}
              canDrop={false}
              parentPath={path}
            />
          ) : (
            <AppAreaFolderNode
              key={item.key}
              folder={item.folder}
              allFolders={allFolders}
              accessibleDocs={accessibleDocs}
              parentPath={path}
              appBaseId={null}
              onTrashAppBase={onTrashAppBase}
            />
          )
        )}
        <FolderChildrenLoading folderId={folder.id} />
      </NavigationPanelTreeNode>
      {shareModalOpen ? (
        <FolderShareModal
          folderId={folder.id}
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
        />
      ) : null}
    </>
  );
};

const PrivateUserFolderNode = ({
  folder,
  allFolders,
  newFolderId,
  setNewFolderId,
  parentPath,
  allowSiblingDrop = true,
  onMoveToFolder,
}: {
  folder: AccessibleFolderMeta;
  allFolders: AccessibleFolderMeta[];
  newFolderId: string | null;
  setNewFolderId: (folderId: string | null) => void;
  parentPath: string[];
  allowSiblingDrop?: boolean;
  onMoveToFolder: (
    data: DropTargetDropEvent<AffineDNDData>,
    targetFolderId: string,
    position?: PrivateDropPosition
  ) => Promise<void>;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const navigationPanelService = useService(NavigationPanelService);
  const areaSortMode = useNavigationPanelSidebarSortMode();
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderStore = useService(FolderStore);
  const showFolderTrashToast = useTrashFolderToast();
  const authService = useService(AuthService);
  const pageHelper = usePageHelper();
  const account = useLiveData(authService.session.account$);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const path = useMemo(
    () => [...parentPath, `folder-${folder.id}`],
    [folder.id, parentPath]
  );
  const collapsed = useSidebarSearchCollapsed(
    useLiveData(navigationPanelService.collapsed$(path)),
    path
  );
  const setCollapsed = useCallback(
    (value: boolean) => navigationPanelService.setCollapsed(path, value),
    [navigationPanelService, path]
  );

  const childFolders = useMemo(
    () =>
      allFolders.filter(
        child =>
          child.parentId === folder.id && child.systemKind !== 'private_mail'
      ),
    [allFolders, folder.id]
  );
  const docIds = useMemo(() => folder.docIds ?? [], [folder.docIds]);
  const accessibleDocs$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAllAccessibleDocs(),
        accessibleDocCache.getCachedDocs()
      ),
    [accessibleDocCache]
  );
  const accessibleDocs = useLiveData(accessibleDocs$);
  const docTitleById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.title])),
    [accessibleDocs]
  );
  const childItems = useMemo<PrivateSidebarChildItem[]>(
    () => [
      ...docIds.map(docId => ({
        key: sidebarManualOrderKey('doc', docId),
        label: docTitleById.get(docId) ?? '',
        type: 'doc' as const,
        docId,
      })),
      ...childFolders.map(child => ({
        key: sidebarManualOrderKey('folder', child.id),
        label: child.name,
        type: 'folder' as const,
        folder: child,
      })),
    ],
    [childFolders, docIds, docTitleById]
  );
  const {
    orderedItems: orderedChildItems,
    placeItem: placeChildItem,
    sortMode: childSortMode,
  } = useSidebarManualOrder(
    `streamlain:private-sidebar-manual-order:${workspace.id}:${folder.id}`,
    childItems,
    compareSidebarManualOrderItems,
    { sortMode: areaSortMode ?? undefined }
  );

  useEffect(() => {
    if (collapsed !== false || folder.hasChildren !== true) {
      return;
    }

    void accessibleFolderCache
      .fetchFolderChildren(folder.id, {
        diagnosticReason: 'private-sidebar-expanded',
      })
      .catch(() => {});
  }, [accessibleFolderCache, collapsed, folder.hasChildren, folder.id]);

  const refreshPrivateFolders = useCallback(async () => {
    await accessibleFolderCache.fetchAccessibleFolders();
  }, [accessibleFolderCache]);

  const applyDocPermissions = useCallback(
    (docId: string, config: DocCreateConfig) => {
      void objectPermissionMutation
        .grantPendingAssignments({ id: docId, kind: 'doc' }, config)
        .catch(error => {
          console.error('Failed to apply private document permissions:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
              ](),
          });
        });
    },
    [objectPermissionMutation, t]
  );

  const handleNewDoc = useCallback(
    (config: DocCreateConfig) => {
      const newDoc = pageHelper.createPage(undefined, {
        show: false,
        targetFolderId: folder.id,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddDocToFolder(folder.id, newDoc.id);
      if (childSortMode === 'manual') {
        placeChildItem(sidebarManualOrderKey('doc', newDoc.id));
      }
      applyDocPermissions(newDoc.id, config);
      void refreshPrivateFolders();
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId: workspace.id,
      });
    },
    [
      applyDocPermissions,
      accessibleFolderCache,
      childSortMode,
      folder.id,
      navigationPanelRevealService,
      pageHelper,
      placeChildItem,
      refreshPrivateFolders,
      workspace.id,
    ]
  );

  const handleCreateDocImmediately = useCallback(() => {
    handleNewDoc(createDefaultDocCreateConfig(t['Untitled']()));
  }, [handleNewDoc, t]);

  const handleCreateSubfolder = useCallback(
    (config: FolderCreateConfig) => {
      const folderId = nanoid();
      const snapshot = accessibleFolderCache.captureOptimisticSnapshot();
      accessibleFolderCache.optimisticUpsertFolder(
        createOptimisticPrivateFolderMeta({
          id: folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          parentId: folder.id,
          ownerUserId: account?.id ?? null,
          visibility: config.visibility,
        }),
        { preserveUntilServerAck: true }
      );
      if (childSortMode === 'manual') {
        placeChildItem(sidebarManualOrderKey('folder', folderId));
      }
      setNewFolderId(folderId);
      setCollapsed(false);

      void folderMutationOutbox
        .enqueueCreateFolder({
          folderId,
          name: config.name,
          parentId: folder.id,
          visibility: config.visibility,
          inheritPermissions: config.inheritPermissions,
        })
        .then(() =>
          enqueueFolderCreatePermissionGrants(
            objectPermissionMutation,
            folderId,
            config
          ).catch(error => {
            console.error(
              'Failed to apply private subfolder permissions:',
              error
            );
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.folder-permissions-failed'
                ](),
            });
          })
        )
        .catch(error => {
          console.error('Failed to create private subfolder:', error);
          accessibleFolderCache.restoreOptimisticSnapshot(snapshot);
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.createFailed'](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      childSortMode,
      folder.id,
      folderMutationOutbox,
      objectPermissionMutation,
      placeChildItem,
      setCollapsed,
      setNewFolderId,
      t,
    ]
  );

  const handleCreateSubfolderImmediately = useCallback(() => {
    handleCreateSubfolder(
      createDefaultFolderCreateConfig(
        'private-folder',
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [handleCreateSubfolder, t]);

  const handleRename = useCallback(
    async (name: string) => {
      const nextName = name.trim();
      if (!nextName || nextName === folder.name) {
        return;
      }
      const snapshot = accessibleFolderCache.captureOptimisticSnapshot();
      accessibleFolderCache.optimisticRenameFolder(folder.id, nextName);
      try {
        await folderMutationOutbox.enqueueRenameFolder(folder.id, nextName);
        setNewFolderId(null);
      } catch (error) {
        accessibleFolderCache.restoreOptimisticSnapshot(snapshot);
        throw error;
      }
    },
    [
      accessibleFolderCache,
      folder.id,
      folder.name,
      folderMutationOutbox,
      setNewFolderId,
    ]
  );

  const handleTrashFolder = useCallback(() => {
    const isCloudWorkspace = workspace.flavour !== 'local';
    void moveFolderToTrashLocalFirstAction({
      accessibleDocCache,
      accessibleFolderCache,
      folderMutationOutbox,
      folderId: folder.id,
      folderStore,
      canTrashFolder: true,
      canUseYjsFolderRoute: !isCloudWorkspace,
      notifyMoveFailed: () => {
        notify.error({
          title: t['com.streamlain.rootAppSidebar.privateFolder.moveFailed'](),
        });
      },
      notifyRestoreFailed: () => {
        notify.error({
          title:
            t['com.streamlain.rootAppSidebar.privateFolder.restoreFailed'](),
        });
      },
      showFolderTrashToast,
      visibility: 'private',
      logError: console.error,
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    folder.id,
    folderMutationOutbox,
    folderStore,
    showFolderTrashToast,
    t,
    workspace.flavour,
  ]);

  const operations = useMemo(
    () => [
      {
        index: 0,
        inline: true,
        view: (
          <OrganizeCreateMenu
            actions={buildSidebarCreateActions({
              document: {
                label:
                  t['com.streamlain.rootAppSidebar.organize.create.document'](),
                onSelect: handleCreateDocImmediately,
              },
              folder: {
                label:
                  t['com.streamlain.rootAppSidebar.organize.create.folder'](),
                onSelect: handleCreateSubfolderImmediately,
              },
            })}
            testId="private-folder-create-button"
            tooltip={t[
              'com.streamlain.rootAppSidebar.organize.create.tooltip'
            ]()}
          />
        ),
      },
      {
        index: 200,
        view: (
          <ComponentMenuItem
            data-testid="private-folder-menu-configure"
            prefixIcon={<ShareIcon />}
            onClick={() => setShareModalOpen(true)}
          >
            {t['com.streamlain.share-menu.shareButton']()}
          </ComponentMenuItem>
        ),
      },
      {
        index: 9999,
        view: (
          <ComponentMenuItem
            data-testid="private-folder-menu-trash"
            data-ui-delete="document"
            prefixIcon={<DeleteIcon />}
            type="danger"
            onClick={handleTrashFolder}
          >
            {t['com.streamlain.moveToTrash.title']()}
          </ComponentMenuItem>
        ),
      },
    ],
    [
      handleCreateDocImmediately,
      handleCreateSubfolderImmediately,
      handleTrashFolder,
      t,
    ]
  );

  const dndData = useMemo(
    () =>
      ({
        draggable: {
          entity: {
            type: 'folder',
            id: folder.id,
          },
          folderTrash: {
            canTrashFolder: true,
          },
          from: {
            at: 'navigation-panel:private:folder-node',
            nodeId: folder.id,
            folderName: folder.name,
          },
        },
        dropTarget: {
          at: 'navigation-panel:organize:folder',
        },
      }) satisfies AffineDNDData,
    [folder.id, folder.name]
  );
  const selectionItem = useMemo<NavigationPanelTreeSelectionItem>(
    () => ({
      entity: {
        type: 'folder',
        id: folder.id,
      },
      folderTrash: {
        canTrashFolder: true,
      },
      from: {
        at: 'navigation-panel:private:folder-node',
        nodeId: folder.id,
        folderName: folder.name,
      },
    }),
    [folder.id, folder.name]
  );

  const handleCanDrop = useMemo<DropTargetOptions<AffineDNDData>['canDrop']>(
    () => args => {
      if (
        !allowSiblingDrop &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      return canDropPrivateEntity(args, {
        targetFolderId: folder.id,
        allFolders,
        canDropDoc: docArgs => {
          const entity = docArgs.source.data.entity;
          return (
            entity?.type === 'doc' &&
            cachedFolderAllowsDocAdd(workspace.id, folder.id) &&
            cachedFolderAllowsDocRemoval(
              workspace.id,
              accessibleFolderCache,
              entity.id,
              folder.id
            )
          );
        },
      });
    },
    [
      accessibleFolderCache,
      allFolders,
      allowSiblingDrop,
      folder.id,
      workspace.id,
    ]
  );

  const handleChildCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      return canDropPrivateEntity(args, {
        targetFolderId: folder.id,
        allFolders,
        allowMakeChild: false,
        canDropDoc: docArgs => {
          const entity = docArgs.source.data.entity;
          return (
            entity?.type === 'doc' &&
            cachedFolderAllowsDocAdd(workspace.id, folder.id) &&
            cachedFolderAllowsDocRemoval(
              workspace.id,
              accessibleFolderCache,
              entity.id,
              folder.id
            )
          );
        },
      });
    },
    [accessibleFolderCache, allFolders, folder.id, workspace.id]
  );

  const handleDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      const position = getPrivateDropPosition(data, 'folder', folder.id);
      const targetFolderId = position ? folder.parentId : folder.id;
      if (!targetFolderId) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          onMoveToFolder(itemData, targetFolderId, position).catch(error => {
            console.error('Failed to move item to private folder:', error);
          });
        });
        return;
      }
      onMoveToFolder(data, targetFolderId, position).catch(error => {
        console.error('Failed to move item to private folder:', error);
      });
    },
    [folder.id, folder.parentId, onMoveToFolder]
  );

  const handleDropOnDoc = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, docId: string) => {
      const position = getPrivateDropPosition(data, 'doc', docId);
      if (!position) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          const entity = itemData.source.data.entity;
          if (entity?.type === 'doc' || entity?.type === 'folder') {
            placeChildItem(
              sidebarManualOrderKey(entity.type, entity.id),
              toSidebarOrderPosition(position)
            );
          }
          onMoveToFolder(itemData, folder.id, position).catch(error => {
            console.error('Failed to move item inside private folder:', error);
          });
        });
        return;
      }
      const entity = data.source.data.entity;
      if (entity?.type === 'doc' || entity?.type === 'folder') {
        placeChildItem(
          sidebarManualOrderKey(entity.type, entity.id),
          toSidebarOrderPosition(position)
        );
      }
      onMoveToFolder(data, folder.id, position).catch(error => {
        console.error('Failed to move item inside private folder:', error);
      });
    },
    [folder.id, onMoveToFolder, placeChildItem]
  );

  const handleDropOnFolder = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, childFolderId: string) => {
      const position = getPrivateDropPosition(data, 'folder', childFolderId);
      if (!position) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          const entity = itemData.source.data.entity;
          if (entity?.type === 'doc' || entity?.type === 'folder') {
            placeChildItem(
              sidebarManualOrderKey(entity.type, entity.id),
              toSidebarOrderPosition(position)
            );
          }
          onMoveToFolder(itemData, folder.id, position).catch(error => {
            console.error('Failed to move item inside private folder:', error);
          });
        });
        return;
      }
      const entity = data.source.data.entity;
      if (entity?.type === 'doc' || entity?.type === 'folder') {
        placeChildItem(
          sidebarManualOrderKey(entity.type, entity.id),
          toSidebarOrderPosition(position)
        );
      }
      onMoveToFolder(data, folder.id, position).catch(error => {
        console.error('Failed to move item inside private folder:', error);
      });
    },
    [folder.id, onMoveToFolder, placeChildItem]
  );

  return (
    <>
      <NavigationPanelTreeNode
        icon={PrivateFolderIcon}
        name={folder.name}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        renameable
        defaultRenaming={folder.id === newFolderId}
        onRenamingChange={renaming => {
          if (!renaming && folder.id === newFolderId) {
            setNewFolderId(null);
          }
        }}
        onRename={name => {
          handleRename(name).catch(error => {
            console.error('Failed to rename private folder:', error);
          });
        }}
        operations={operations}
        revealOperations
        childrenEmpty={isFolderKnownEmpty(
          folder.hasChildren,
          orderedChildItems.length
        )}
        childrenPlaceholder={
          folder.hasChildren === true ? undefined : (
            <FolderEmpty canDrop={handleCanDrop} onDrop={handleDrop} />
          )
        }
        dndData={dndData}
        selectionItem={selectionItem}
        canDrop={handleCanDrop}
        onDrop={handleDrop}
        dropEffect={privateFolderDropEffect}
        data-testid={`navigation-panel-private-folder-${folder.id}`}
        explorerIconConfig={{
          where: 'folder',
          id: folder.id,
        }}
      >
        {orderedChildItems.map(item =>
          item.type === 'doc' ? (
            <NavigationPanelDocNode
              key={item.key}
              docId={item.docId}
              hideLinkedChildren
              location={{
                at: 'navigation-panel:private:folder-node',
                nodeId: folder.id,
              }}
              canDrop={handleChildCanDrop}
              onDrop={data => handleDropOnDoc(data, item.docId)}
              dropEffect={privateFolderDropEffect}
              parentPath={path}
            />
          ) : (
            <PrivateUserFolderNode
              key={item.key}
              folder={item.folder}
              allFolders={allFolders}
              newFolderId={newFolderId}
              setNewFolderId={setNewFolderId}
              parentPath={path}
              allowSiblingDrop
              onMoveToFolder={(data, targetFolderId, position) => {
                if (
                  position?.entityType === 'folder' &&
                  position.entityId === item.folder.id &&
                  targetFolderId === folder.id
                ) {
                  handleDropOnFolder(data, item.folder.id);
                  return Promise.resolve();
                }
                return onMoveToFolder(data, targetFolderId, position);
              }}
            />
          )
        )}
        <FolderChildrenLoading folderId={folder.id} />
      </NavigationPanelTreeNode>
      <OutboxDeletionPendingLine kind="folder" id={folder.id} />
      {shareModalOpen && (
        <FolderShareModal
          folderId={folder.id}
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
        />
      )}
    </>
  );
};

function isFolderDescendant(
  folderId: string,
  possibleAncestorId: string,
  folders: AccessibleFolderMeta[]
): boolean {
  let current = folders.find(folder => folder.id === folderId);
  while (current?.parentId) {
    if (current.parentId === possibleAncestorId) {
      return true;
    }
    current = folders.find(folder => folder.id === current?.parentId);
  }
  return false;
}

interface SidebarCreatePlaceholderProps extends HTMLAttributes<HTMLDivElement> {
  actionText?: string;
  icon:
    | ((props: SVGProps<SVGSVGElement>) => JSX.Element)
    | ReactElement<SVGAttributes<SVGElement>>;
  message: string;
  messageTestId?: string;
  onActionClick?: () => void;
  testId?: string;
}

const SidebarCreatePlaceholder = forwardRef<
  HTMLDivElement,
  SidebarCreatePlaceholderProps
>(function SidebarCreatePlaceholder(
  {
    actionText,
    className,
    icon,
    message,
    messageTestId,
    onActionClick,
    testId,
    ...attrs
  },
  ref
) {
  return (
    <NavigationPanelEmptySection
      className={
        className
          ? `${areaCreatePlaceholder} ${className}`
          : areaCreatePlaceholder
      }
      data-testid={testId}
      ref={ref}
      icon={icon}
      message={message}
      messageTestId={messageTestId}
      actionText={actionText}
      onActionClick={onActionClick}
      {...attrs}
    />
  );
});

export const DocumentsSidebarProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceAreaCache = useService(WorkspaceAreaCacheStore);
  const permissionService = useService(WorkspacePermissionService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const [newAreaRootFolderId, setNewAreaRootFolderId] = useState<string | null>(
    null
  );
  const navigationPanelService = useService(NavigationPanelService);
  const areas = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          workspaceAreaCache.watchAreas(),
          workspaceAreaCache.getCachedAreas()
        ),
      [workspaceAreaCache]
    )
  );
  const role = useLiveData(permissionService.permission.role$);
  useEffect(() => {
    markSidebarPerformanceSignal('source:areas');
  }, [areas]);
  useEffect(() => {
    markSidebarPerformanceSignal('source:permission-role');
  }, [role]);
  const effectiveRole = role ?? workspace.meta.role ?? null;

  useEffect(() => {
    if (workspace.flavour === 'local') {
      return;
    }
    accessibleFolderCache.fetchAccessibleFolders().catch(error => {
      if (!isBackendUnavailableError(error)) {
        console.error('Failed to refresh workspace areas:', error);
      }
    });
    accessibleDocCache.fetchAccessibleDocs().catch(error => {
      if (!isBackendUnavailableError(error)) {
        console.error('Failed to refresh workspace area docs:', error);
      }
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    areas?.length,
    workspace.flavour,
  ]);

  const refreshAreas = useCallback(() => {
    workspaceAreaCache.refresh().catch(error => {
      if (!isBackendUnavailableError(error)) {
        console.error('Failed to refresh workspace areas:', error);
      }
    });
    accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
  }, [accessibleFolderCache, workspaceAreaCache]);
  const canCreateArea =
    workspace.flavour !== 'local' &&
    (effectiveRole === Permission.Owner || effectiveRole === Permission.Admin);
  const teamspacesGate = usePlanGate([FEATURE_KEY_TEAMSPACES]);
  const canOpenPlans = useCanOpenWorkspacePlans();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const createArea = useCallback(
    async (name?: string) => {
      if (teamspacesGate.status === 'locked') {
        // Plan gate: creation needs the Team plan. Owners/admins land on the
        // Plans tab; everyone else gets the plan hint as a toast.
        if (canOpenPlans) {
          workspaceDialogService.open('setting', {
            activeTab: 'workspace:plans',
          });
        } else {
          notify.error({
            title: t['com.streamlain.rootAppSidebar.teamspace.create'](),
            message: teamspacesGate.requiredPlan
              ? t['com.streamlain.feature-gate.upgrade-description']({
                  plan: teamspacesGate.requiredPlan,
                })
              : t['com.streamlain.feature-gate.not-in-plan'](),
          });
        }
        return;
      }
      try {
        const area = await workspaceAreaCache.createTeamspace(
          name?.trim() ||
            t['com.streamlain.rootAppSidebar.teamspace.new-default']()
        );
        setNewAreaRootFolderId(area.rootFolderId);
        navigationPanelService.setCollapsed(['teamAreas'], false);
        navigationPanelService.setCollapsed(
          ['teamspace', area.rootFolderId],
          false
        );
        notify.success({
          title: t['com.streamlain.rootAppSidebar.teamspace.created'](),
        });
      } catch (error) {
        console.error('Failed to create workspace area:', error);
        notify.error({
          title: t['com.streamlain.rootAppSidebar.teamspace.createFailed'](),
        });
      }
    },
    [
      canOpenPlans,
      navigationPanelService,
      t,
      teamspacesGate,
      workspaceAreaCache,
      workspaceDialogService,
    ]
  );
  const value = useMemo<WorkspaceAreaCreationContextValue>(
    () => ({
      areas: areas ?? [],
      canCreateArea,
      createArea,
      newAreaRootFolderId,
      clearNewAreaRootFolderId: () => setNewAreaRootFolderId(null),
      refreshAreas,
    }),
    [areas, canCreateArea, createArea, newAreaRootFolderId, refreshAreas]
  );

  return (
    <WorkspaceAreaCreationContext.Provider value={value}>
      {children}
    </WorkspaceAreaCreationContext.Provider>
  );
};

/** Journals keep their existing registry and saved expansion, presented as a folder. */
const JournalSidebarFolder = () => {
  const navigation = useService(NavigationPanelService);
  const journalDocIds = useLiveData(
    useService(JournalService).allJournalDocIds$
  );
  const t = useI18n();
  const journalService = useService(JournalService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const folderStore = useService(FolderStore);
  const showFolderTrashToast = useTrashFolderToast();
  const account = useLiveData(useService(AuthService).session.account$);
  const folder = useLiveData(
    useMemo(
      () => journalService.journalFolder$(account?.id ?? null),
      [journalService, account?.id]
    )
  );
  const canTrashFolder =
    !!folder &&
    !folder.immutable &&
    !folder.metadataOnly &&
    folder.ownerUserId === account?.id;
  const handleTrash = useCallback(() => {
    if (!folder || !canTrashFolder) return;
    void moveFolderToTrashLocalFirstAction({
      accessibleDocCache,
      accessibleFolderCache,
      folderMutationOutbox,
      folderId: folder.id,
      folderStore,
      canTrashFolder,
      canUseYjsFolderRoute: false,
      notifyMoveFailed: () =>
        notify.error({
          title: t['com.streamlain.rootAppSidebar.privateFolder.moveFailed'](),
        }),
      notifyRestoreFailed: () =>
        notify.error({
          title:
            t['com.streamlain.rootAppSidebar.privateFolder.restoreFailed'](),
        }),
      showFolderTrashToast,
      visibility: 'private',
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    canTrashFolder,
    folder,
    folderMutationOutbox,
    folderStore,
    showFolderTrashToast,
    t,
  ]);
  const operations = useMemo(
    () =>
      canTrashFolder
        ? [
            {
              index: 9999,
              view: (
                <ComponentMenuItem
                  data-testid="journal-folder-menu-trash"
                  data-ui-delete="document"
                  prefixIcon={<DeleteIcon />}
                  type="danger"
                  onClick={handleTrash}
                >
                  {t['com.streamlain.moveToTrash.title']()}
                </ComponentMenuItem>
              ),
            },
          ]
        : [],
    [canTrashFolder, handleTrash, t]
  );
  const path = useMemo(() => ['journal'], []);
  const storedCollapsed = useLiveData(navigation.collapsed$(path));
  const collapsed = useSidebarSearchCollapsed(storedCollapsed, path);
  const search = useSidebarSearchState();
  const headerRef = useRef<HTMLDivElement>(null);
  const setCollapsed = useCallback(
    (value: boolean) => {
      if (!search.active) navigation.setCollapsed(path, value);
    },
    [navigation, path, search.active]
  );
  const expand = useCallback(() => {
    navigation.setCollapsed(['personal'], false);
    setCollapsed(false);
  }, [navigation, setCollapsed]);
  const roots = useMemo(
    () => journalDocIds.map(id => ({ kind: 'doc' as const, id, label: id })),
    [journalDocIds]
  );
  const entries = useNavigationTreeEntries(roots, path);
  return (
    <NavigationTreeSectionOwner
      path={path}
      label="Journals"
      entries={entries}
      headerRef={headerRef}
      headerSelector='[data-navigation-panel-tree-node-trigger="true"] [role="button"]'
      expanded={!collapsed}
      onExpand={expand}
    >
      <div ref={headerRef}>
        <NavigationPanelTreeRoot>
          <NavigationPanelTreeNode
            icon={PrivateFolderIcon}
            revealOperations
            operations={operations}
            renameable={false}
            name="Journals"
            data-testid="sidebar-journal"
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            reorderable={false}
            selectable={false}
            canDrop={false}
            childrenEmpty={journalDocIds.length === 0}
          >
            {journalDocIds.map(docId => (
              <NavigationPanelDocNode
                key={docId}
                docId={docId}
                disableDrag
                hideLinkedChildren
                parentPath={path}
              />
            ))}
          </NavigationPanelTreeNode>
          {folder && <OutboxDeletionPendingLine kind="folder" id={folder.id} />}
        </NavigationPanelTreeRoot>
      </div>
    </NavigationTreeSectionOwner>
  );
};

const PersonalSidebarSections = ({
  collectionsEnabled,
  tagsEnabled,
  onPrivateActionsChange,
}: {
  collectionsEnabled: boolean;
  tagsEnabled: boolean;
  onPrivateActionsChange: (actions: ReactNode) => void;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const journalService = useService(JournalService);
  const journalDates = useLiveData(journalService.allJournalDates$);
  const { isFeatureAllowed } = useFeatureGate();
  const profile = useWorkspaceInfo(workspace.meta);
  const privateAreaVisible =
    !(profile?.membershipKind === 'house_guests') &&
    workspace.flavour !== 'local' &&
    isFeatureAllowed(FEATURE_KEY_PRIVATE_FOLDERS);
  const journalVisible =
    !(profile?.membershipKind === 'house_guests') && journalDates.size > 0;
  const personalSectionItems = useMemo<WorkspaceSidebarAreaSectionItem[]>(
    () => [
      {
        key: 'area:migration-favorites',
        label: t['com.streamlain.rootAppSidebar.migration-data'](),
        render: () => <NavigationPanelMigrationFavorites revealOnExpand />,
      },
      ...(journalVisible
        ? [
            {
              key: 'area:journal',
              label: 'Journals',
              render: () => <JournalSidebarFolder />,
            },
          ]
        : []),
      ...(privateAreaVisible
        ? [
            {
              key: 'area:private',
              label: t['com.streamlain.rootAppSidebar.privateFolder'](),
              render: () => (
                <PrivateSidebarSection
                  hideJournalFolder
                  onActionsChange={onPrivateActionsChange}
                />
              ),
            },
          ]
        : []),
      ...(tagsEnabled
        ? [
            {
              key: 'area:tags',
              label: t['com.streamlain.rootAppSidebar.tags'](),
              render: () => <NavigationPanelTags revealOnExpand />,
            },
          ]
        : []),
      ...(collectionsEnabled
        ? [
            {
              key: 'area:collections',
              label: t['com.streamlain.rootAppSidebar.collections'](),
              render: () => <NavigationPanelCollections revealOnExpand />,
            },
          ]
        : []),
    ],
    [
      collectionsEnabled,
      journalVisible,
      onPrivateActionsChange,
      privateAreaVisible,
      t,
      tagsEnabled,
    ]
  );

  return (
    <SidebarReorderableList
      className={workspaceSharedAreaGroup}
      scope="root-sidebar:personal-areas"
    >
      {personalSectionItems.map(item => (
        <div className={workspaceAreaSection} key={item.key}>
          {item.render()}
        </div>
      ))}
    </SidebarReorderableList>
  );
};

const TeamAreaSections = ({
  onActionsChange,
}: {
  onActionsChange: (actions: ReactNode) => void;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceAreaCreation = useWorkspaceAreaCreation();
  const workspaceAreas = workspaceAreaCreation?.areas;
  const refreshAreas =
    workspaceAreaCreation?.refreshAreas ?? noopRefreshWorkspaceAreas;
  const newAreaRootFolderId =
    workspaceAreaCreation?.newAreaRootFolderId ?? null;
  const clearNewAreaRootFolderId =
    workspaceAreaCreation?.clearNewAreaRootFolderId ??
    noopClearNewWorkspaceArea;

  const teamspaces = useMemo(
    () =>
      (workspaceAreas ?? [])
        .filter(area => area.kind === 'teamspace')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [workspaceAreas]
  );
  const fixedAreaSectionItems = useMemo<WorkspaceSidebarAreaSectionItem[]>(
    () => [
      {
        key: 'area:public',
        label: t['com.streamlain.rootAppSidebar.publicArea'](),
        render: () => <NavigationPanelOrganize />,
      },
    ],
    [t]
  );
  const movableAreaSectionItems = useMemo<WorkspaceSidebarAreaSectionItem[]>(
    () =>
      teamspaces.map(area => ({
        key: `area:teamspace:${area.rootFolderId}`,
        label: area.name,
        render: (reorderActions?: SidebarAreaReorderActions) => (
          <TeamspaceSidebarSection
            area={area}
            defaultRenaming={area.rootFolderId === newAreaRootFolderId}
            onRenamingChange={renaming => {
              if (!renaming && area.rootFolderId === newAreaRootFolderId) {
                clearNewAreaRootFolderId();
              }
            }}
            onRefreshAreas={refreshAreas}
            reorderActions={reorderActions}
          />
        ),
      })),
    [clearNewAreaRootFolderId, newAreaRootFolderId, refreshAreas, teamspaces]
  );
  const {
    orderedItems: orderedMovableAreaItems,
    placeItem: placeMovableAreaSection,
    sortMode,
    setSortMode,
  } = useSidebarManualOrder(
    `streamlain:workspace-area-section-order:${workspace.id}`,
    movableAreaSectionItems,
    compareWorkspaceSidebarAreaSections,
    { defaultSortMode: 'manual', manualBase: 'source' }
  );
  const teamActions = useMemo(
    () => (
      <SidebarAreaHeaderActions
        options={{
          sortMode,
          onSortModeChange: setSortMode,
          testId: 'navigation-panel-team-areas-options-button',
        }}
      />
    ),
    [sortMode, setSortMode]
  );
  useLayoutEffect(() => {
    onActionsChange(teamActions);
    return () => onActionsChange(null);
  }, [onActionsChange, teamActions]);
  const connectivityStatus = useConnectivityStatus();
  const createAreaTarget = useMemo(
    () => ({
      id: 'new-teamspace',
      section: 'teamAreas' as const,
      label: t['com.streamlain.rootAppSidebar.teamspace.create'](),
      create: workspaceAreaCreation?.canCreateArea
        ? (name: string) => {
            if (!connectivityStatus.backendAvailable) {
              notify.error({
                title: t['com.streamlain.settings.connection-required.title'](),
                message:
                  t[
                    'com.streamlain.settings.connection-required.description'
                  ](),
              });
              return;
            }
            void workspaceAreaCreation.createArea(name);
          }
        : undefined,
    }),
    [connectivityStatus.backendAvailable, t, workspaceAreaCreation]
  );
  useSidebarCreateTarget(createAreaTarget);
  const renderedFixedAreaItems = fixedAreaSectionItems.map(item => (
    <div className={workspaceAreaSection} key={item.key}>
      {item.render()}
    </div>
  ));
  const renderedMovableAreaItems = orderedMovableAreaItems.map(
    (item, index) => {
      const moveUp = () => {
        const before = orderedMovableAreaItems[index - 1];
        if (before) {
          placeMovableAreaSection(item.key, {
            at: 'before',
            key: before.key,
          });
        }
      };
      const moveDown = () => {
        const after = orderedMovableAreaItems[index + 1];
        if (after) {
          placeMovableAreaSection(item.key, {
            at: 'after',
            key: after.key,
          });
        }
      };
      return (
        <SidebarReorderableItem
          canMoveDown={index < orderedMovableAreaItems.length - 1}
          canMoveUp={index > 0}
          className={workspaceAreaSection}
          itemKey={item.key}
          key={item.key}
          label={item.label}
          onMoveDown={moveDown}
          onMoveUp={moveUp}
          onPlace={placeMovableAreaSection}
          scope="root-sidebar:workspace-areas"
          testIdPrefix="workspace-area"
        >
          {() =>
            item.render({
              canMoveDown: index < orderedMovableAreaItems.length - 1,
              canMoveUp: index > 0,
              onMoveDown: moveDown,
              onMoveUp: moveUp,
            })
          }
        </SidebarReorderableItem>
      );
    }
  );
  const renderedAreaItems = [
    ...renderedFixedAreaItems,
    ...(orderedMovableAreaItems.length > 0
      ? [
          <div
            aria-hidden="true"
            className={workspaceAreaSeparatorRow}
            key="area:public-dynamic-separator"
          >
            <div className={workspaceAreaSeparatorLine} />
          </div>,
        ]
      : []),
    ...renderedMovableAreaItems,
  ];

  return (
    <SidebarReorderableList
      className={workspaceSharedAreaGroup}
      scope="root-sidebar:workspace-areas"
    >
      {renderedAreaItems}
    </SidebarReorderableList>
  );
};

const TeamspaceSidebarSection = ({
  area,
  defaultRenaming = false,
  onRenamingChange,
  onRefreshAreas,
  reorderActions,
}: {
  area: WorkspaceAreaRef;
  defaultRenaming?: boolean;
  onRenamingChange?: (renaming: boolean) => void;
  onRefreshAreas: () => void;
  reorderActions?: SidebarAreaReorderActions;
}) => {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const framework = useFramework();
  const workspace = useService(WorkspaceService).workspace;
  const connectivityStatus = useConnectivityStatus();
  const navigationPanelService = useService(NavigationPanelService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const confirmAppDataMove = useConfirmAppDataMove();
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderStore = useService(FolderStore);
  const authService = useService(AuthService);
  const account = useLiveData(authService.session.account$);
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const { canCreateDocEffective } = useWorkspaceCreateDocPermission();
  const pageHelper = usePageHelper();
  const selectionContext = useContext(NavigationPanelTreeSelectionContext);
  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [renaming, setRenaming] = useState(defaultRenaming);
  const [protectAreaRenameFocus, setProtectAreaRenameFocus] =
    useState(defaultRenaming);
  const workspaceAreaCache = useService(WorkspaceAreaCacheStore);
  const path = useMemo(
    () => ['teamspace', area.rootFolderId],
    [area.rootFolderId]
  );
  const collapsed = useSidebarSearchCollapsed(
    useLiveData(navigationPanelService.collapsed$(path)),
    path
  );
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const accessibleDocs = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleDocCache.watchAllAccessibleDocs(),
          accessibleDocCache.getCachedDocs()
        ),
      [accessibleDocCache]
    )
  );
  const teamspaceRoot = useMemo(
    () =>
      accessibleFolders.find(folder => folder.id === area.rootFolderId) ?? null,
    [accessibleFolders, area.rootFolderId]
  );
  const docTitleById = useMemo(
    () =>
      new Map(
        accessibleDocs
          .filter(doc => !doc.trashed)
          .map(doc => [doc.id, doc.title])
      ),
    [accessibleDocs]
  );
  const teamMailboxFolders = useMemo(
    () =>
      accessibleFolders.filter(
        folder =>
          folder.parentId === area.rootFolderId &&
          folder.systemKind === TEAM_MAILBOXES_FOLDER_SYSTEM_KIND
      ),
    [accessibleFolders, area.rootFolderId]
  );
  const teamMailboxFolderIds = useMemo(
    () => teamMailboxFolders.map(folder => folder.id),
    [teamMailboxFolders]
  );
  useMaterializeFolderChildren(
    accessibleFolderCache,
    teamMailboxFolderIds,
    'root-sidebar:team-mail-folder'
  );
  const childFolders = useMemo(
    () =>
      accessibleFolders
        .filter(
          folder =>
            folder.parentId === area.rootFolderId &&
            folder.systemKind !== TEAMSPACE_ROOT_SYSTEM_KIND &&
            (folder.systemKind !== TEAM_MAILBOXES_FOLDER_SYSTEM_KIND ||
              (folder.docIds ?? []).some(
                docId => !hiddenMailFolderDocIds.has(docId)
              ))
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [accessibleFolders, area.rootFolderId, hiddenMailFolderDocIds]
  );

  useEffect(() => {
    accessibleFolderCache
      .fetchFolderChildren(area.rootFolderId)
      .catch(() => {});
  }, [accessibleFolderCache, area.rootFolderId]);

  const rootItems = useMemo<AppAreaSidebarChildItem[]>(
    () => [
      ...childFolders.map(folder => ({
        key: sidebarManualOrderKey('folder', folder.id),
        label: folder.name,
        type: 'folder' as const,
        folder,
      })),
      ...((teamspaceRoot?.docIds ?? []) as string[])
        .filter(
          docId => docTitleById.has(docId) && !hiddenMailFolderDocIds.has(docId)
        )
        .map(docId => ({
          key: sidebarManualOrderKey('doc', docId),
          label: docTitleById.get(docId) ?? '',
          type: 'doc' as const,
          docId,
        })),
    ],
    [childFolders, docTitleById, hiddenMailFolderDocIds, teamspaceRoot?.docIds]
  );
  const {
    orderedItems,
    placeItem: placeRootItem,
    setSortMode,
    sortMode,
  } = useSidebarManualOrder(
    `streamlain:teamspace-sidebar-manual-order:${workspace.id}:${area.rootFolderId}`,
    rootItems,
    compareSidebarManualOrderItems,
    { manualBase: 'source' }
  );
  const canCreateFolder = area.permissions.Folder_CreateSubfolder;
  const canCreateDocument =
    canCreateDocEffective && area.permissions.Folder_AddDoc;
  const canMoveRootFolder = area.permissions.Folder_CreateSubfolder;
  const canMoveDocToRoot = useCallback(
    (docId: string) =>
      cachedFolderAllowsDocAdd(
        workspace.id,
        area.rootFolderId,
        area.permissions
      ) &&
      cachedFolderAllowsDocRemoval(
        workspace.id,
        accessibleFolderCache,
        docId,
        area.rootFolderId
      ),
    [accessibleFolderCache, area.permissions, area.rootFolderId, workspace.id]
  );
  const canManageUsers =
    area.permissions.Folder_Users_Manage || area.permissions.Folder_Users_Read;
  // Renaming and trashing an area are both "manage the area" on the server
  // (WorkspaceAreaService.assertTeamspaceManager): one gate for both.
  const canManageArea =
    area.kind === 'teamspace' &&
    !area.immutable &&
    !area.systemManaged &&
    (area.effectiveRole === FolderRole.Owner ||
      area.effectiveRole === FolderRole.Admin) &&
    connectivityStatus.backendAvailable;

  const handleTrashArea = useCallback(() => {
    openConfirmModal({
      title: t['com.streamlain.moveToTrash.title'](),
      description: t['com.streamlain.moveToTrash.confirmModal.description']({
        title: area.name,
      }),
      children: (
        <DocDeleteAppUsage
          framework={framework}
          target={{ kind: 'folder', ids: [area.rootFolderId] }}
          operation="trash"
        />
      ),
      confirmText: t['com.streamlain.moveToTrash.confirmModal.confirm'](),
      cancelText: t['com.streamlain.moveToTrash.confirmModal.cancel'](),
      confirmButtonOptions: { variant: 'error' },
      async onConfirm() {
        try {
          await workspaceAreaCache.trashTeamspace(area.rootFolderId);
          onRefreshAreas();
        } catch (error) {
          console.error('Failed to trash teamspace:', error);
          notify.error({
            title:
              t['com.streamlain.settings.workspace.teamspaces.trash-failed'](),
          });
        }
      },
    });
  }, [
    area.name,
    area.rootFolderId,
    framework,
    onRefreshAreas,
    openConfirmModal,
    t,
    workspaceAreaCache,
  ]);

  useEffect(() => {
    if (defaultRenaming) {
      setProtectAreaRenameFocus(true);
      setRenaming(true);
    }
  }, [defaultRenaming]);

  const handleAreaRenamingChange = useCallback(
    (nextRenaming: boolean) => {
      if (!nextRenaming) {
        setProtectAreaRenameFocus(false);
      }
      setRenaming(nextRenaming);
      onRenamingChange?.(nextRenaming);
    },
    [onRenamingChange]
  );
  const startAreaRename = useCallback(
    (protectInitialFocus: boolean) => {
      setProtectAreaRenameFocus(protectInitialFocus);
      handleAreaRenamingChange(true);
    },
    [handleAreaRenamingChange]
  );
  const handleAreaRename = useCallback(
    (name: string) => {
      const nextName = name.trim();
      if (!nextName || nextName === area.name) return;
      void workspaceAreaCache
        .updateTeamspace(area.rootFolderId, { name: nextName })
        .catch(error => {
          console.error('Failed to rename workspace area:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.rename-failed'
              ](),
          });
        });
    },
    [area.name, area.rootFolderId, t, workspaceAreaCache]
  );

  const handleRootSiblingDrop = useCallback(
    (
      data: DropTargetDropEvent<AffineDNDData>,
      targetKey: string,
      rootPosition: SidebarManualOrderPosition
    ) => {
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleRootSiblingDrop(itemData, targetKey, rootPosition);
        });
        selectionContext?.clearSelection();
        return;
      }

      const entity = data.source.data.entity;
      if (!entity || entity.id === area.rootFolderId) {
        return;
      }
      if (entity.type === 'doc') {
        if (!canMoveDocToRoot(entity.id)) {
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
              ](),
          });
          return;
        }

        const optimisticSnapshot =
          accessibleFolderCache.captureOptimisticSnapshot();
        accessibleFolderCache.optimisticAddDocToFolder(
          area.rootFolderId,
          entity.id,
          rootPosition.key.startsWith('doc:')
            ? {
                at: rootPosition.at,
                docId: targetKey,
              }
            : undefined
        );
        placeRootItem(sidebarManualOrderKey('doc', entity.id), rootPosition);
        navigationPanelService.setCollapsed(path, false);

        void confirmAppDataMove({
          id: entity.id,
          kind: 'doc',
          targetFolderId: area.rootFolderId,
        })
          .then(() =>
            folderMutationOutbox.enqueueAddDocToFolder(
              area.rootFolderId,
              entity.id
            )
          )
          .then(onRefreshAreas)
          .catch(error => {
            accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
            if (isAppDataChangeCancelled(error)) return;
            console.error('Failed to sync doc move to teamspace root:', error);
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
                ](),
            });
          });
        return;
      }

      if (entity.type !== 'folder') {
        return;
      }
      if (!canMoveRootFolder) {
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied'
            ](),
        });
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      const previousInfo = folderStore.getYjsFolderNodeInfo(entity.id);
      const folderName =
        accessibleFolderCache.getCachedFolder(entity.id)?.name ??
        previousInfo?.data ??
        t['Folder']();

      try {
        accessibleFolderCache.optimisticMoveFolder(
          entity.id,
          area.rootFolderId,
          {
            name: folderName,
            systemKind: null,
            visibility: area.visibility,
          },
          rootPosition.key.startsWith('folder:')
            ? {
                at: rootPosition.at,
                folderId: targetKey,
              }
            : undefined,
          {
            name: folderName,
            ownerUserId: account?.id ?? null,
            systemKind: null,
            visibility: area.visibility,
          }
        );
        placeRootItem(sidebarManualOrderKey('folder', entity.id), rootPosition);
        navigationPanelService.setCollapsed(path, false);
      } catch (error) {
        accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
        console.error('Failed to move folder to teamspace root:', error);
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied'
            ](),
        });
        return;
      }

      void confirmAppDataMove({
        id: entity.id,
        kind: 'folder',
        targetFolderId: area.rootFolderId,
      })
        .then(() =>
          folderMutationOutbox.enqueueMoveFolder(entity.id, area.rootFolderId)
        )
        .then(onRefreshAreas)
        .catch(error => {
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          if (isAppDataChangeCancelled(error)) return;
          console.error('Failed to sync folder move to teamspace root:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied'
              ](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      area.rootFolderId,
      area.visibility,
      canMoveDocToRoot,
      canMoveRootFolder,
      confirmAppDataMove,
      folderMutationOutbox,
      folderStore,
      navigationPanelService,
      onRefreshAreas,
      path,
      placeRootItem,
      selectionContext,
      t,
    ]
  );

  const handleRootDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleRootDrop(itemData);
        });
        selectionContext?.clearSelection();
        return;
      }
      handleRootSiblingDrop(data, area.rootFolderId, {
        at: 'before',
        key: '',
      });
    },
    [area.rootFolderId, handleRootSiblingDrop, selectionContext]
  );

  const handleRootFolderDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, folderId: string) => {
      if (
        data.treeInstruction?.type !== 'reorder-above' &&
        data.treeInstruction?.type !== 'reorder-below'
      ) {
        return;
      }
      const at =
        data.treeInstruction.type === 'reorder-below' ? 'after' : 'before';
      handleRootSiblingDrop(data, folderId, {
        at,
        key: sidebarManualOrderKey('folder', folderId),
      });
    },
    [handleRootSiblingDrop]
  );

  const handleRootDocDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, docId: string) => {
      if (
        data.treeInstruction?.type !== 'reorder-above' &&
        data.treeInstruction?.type !== 'reorder-below'
      ) {
        return;
      }
      const at =
        data.treeInstruction.type === 'reorder-below' ? 'after' : 'before';
      handleRootSiblingDrop(data, docId, {
        at,
        key: sidebarManualOrderKey('doc', docId),
      });
    },
    [handleRootSiblingDrop]
  );

  const handleRootChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      if (
        sortMode !== 'manual' &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      const entity = args.source.data.entity;
      if (entity?.type === 'doc') {
        return canMoveDocToRoot(entity.id);
      }
      return (
        canMoveRootFolder &&
        entity?.type === 'folder' &&
        entity.id !== area.rootFolderId
      );
    },
    [area.rootFolderId, canMoveDocToRoot, canMoveRootFolder, sortMode]
  );
  const canDropOnTeamspaceArea =
    sortMode !== 'manual' || orderedItems.length === 0;
  const { dropTargetRef: teamspaceRootContentDropTargetRef } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: { at: 'navigation-panel:organize:root' },
        onDrop: handleRootDrop,
        canDrop: args =>
          canDropOnTeamspaceArea &&
          ((typeof handleRootChildrenCanDrop === 'function'
            ? handleRootChildrenCanDrop(args)
            : handleRootChildrenCanDrop) ??
            false),
        allowExternal: true,
      }),
      [canDropOnTeamspaceArea, handleRootChildrenCanDrop, handleRootDrop]
    );
  const { dropTargetRef: teamspaceRootHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget({
      canDrop: handleRootChildrenCanDrop,
      collapsed,
      onDrop: handleRootDrop,
      path,
    });

  const handleCreateFolder = useCallback(
    (config: FolderCreateConfig) => {
      if (!canCreateFolder) return;
      const folderId = nanoid();
      accessibleFolderCache.optimisticUpsertFolder(
        createOptimisticPrivateFolderMeta({
          id: folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          ownerUserId: account?.id ?? null,
          parentId: area.rootFolderId,
          visibility: config.visibility,
        }),
        { preserveUntilServerAck: true }
      );
      if (sortMode === 'manual') {
        placeRootItem(sidebarManualOrderKey('folder', folderId));
      }
      setNewFolderId(folderId);
      navigationPanelService.setCollapsed(path, false);
      void folderMutationOutbox
        .enqueueCreateFolder({
          folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          parentId: area.rootFolderId,
          visibility: config.visibility,
        })
        .then(() =>
          enqueueFolderCreatePermissionGrants(
            objectPermissionMutation,
            folderId,
            config
          )
        )
        .then(onRefreshAreas)
        .catch(error => {
          console.error('Failed to create teamspace folder:', error);
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.teamspace.folderCreateFailed'](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      area.rootFolderId,
      canCreateFolder,
      folderMutationOutbox,
      navigationPanelService,
      objectPermissionMutation,
      onRefreshAreas,
      path,
      placeRootItem,
      sortMode,
      t,
    ]
  );

  const handleCreateDocument = useCallback(
    (config: DocCreateConfig) => {
      if (!canCreateDocument) return;
      const newDoc = pageHelper.createPage(undefined, {
        show: false,
        targetFolderId: area.rootFolderId,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddDocToFolder(
        area.rootFolderId,
        newDoc.id
      );
      if (sortMode === 'manual') {
        placeRootItem(sidebarManualOrderKey('doc', newDoc.id));
      }
      void objectPermissionMutation
        .grantPendingAssignments({ id: newDoc.id, kind: 'doc' }, config)
        .catch(error => {
          console.error(
            'Failed to apply teamspace document permissions:',
            error
          );
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
              ](),
          });
        });
      navigationPanelService.setCollapsed(path, false);
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId: workspace.id,
      });
      void onRefreshAreas();
    },
    [
      accessibleFolderCache,
      area.rootFolderId,
      canCreateDocument,
      navigationPanelRevealService,
      navigationPanelService,
      objectPermissionMutation,
      onRefreshAreas,
      pageHelper,
      path,
      placeRootItem,
      sortMode,
      t,
      workspace.id,
    ]
  );

  const navigationRoots = useMemo(
    () =>
      rootItems.map(item =>
        item.type === 'folder'
          ? { kind: 'folder' as const, id: item.folder.id, label: item.label }
          : { kind: 'doc' as const, id: item.docId, label: item.label }
      ),
    [rootItems]
  );
  const navigationEntries = useNavigationTreeEntries(
    navigationRoots,
    path,
    hiddenMailFolderDocIds
  );
  const handleCreateDocumentImmediately = useCallback(() => {
    handleCreateDocument(createDefaultDocCreateConfig(t['Untitled']()));
  }, [handleCreateDocument, t]);

  const handleCreateFolderImmediately = useCallback(() => {
    handleCreateFolder(
      createDefaultFolderCreateConfig(
        'teamspace-folder',
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [handleCreateFolder, t]);

  const teamCreateTarget = useMemo(
    () => ({
      id: area.rootFolderId,
      section: 'teamAreas' as const,
      label: `${t['com.streamlain.rootAppSidebar.teamAreas']()} / ${area.name}`,
      document: canCreateDocument
        ? (name: string) => {
            navigationPanelService.setCollapsed(['teamAreas'], false);
            handleCreateDocument(
              createDefaultDocCreateConfig(name || t['Untitled']())
            );
          }
        : undefined,
      folder: canCreateFolder
        ? (name: string) => {
            navigationPanelService.setCollapsed(['teamAreas'], false);
            handleCreateFolder(
              createDefaultFolderCreateConfig(
                'teamspace-folder',
                name ||
                  t['com.streamlain.rootAppSidebar.organize.create.folder']()
              )
            );
          }
        : undefined,
    }),
    [
      area.name,
      area.rootFolderId,
      canCreateDocument,
      canCreateFolder,
      handleCreateDocument,
      handleCreateFolder,
      navigationPanelService,
      t,
    ]
  );
  const centralCreation = useSidebarCreateTarget(teamCreateTarget);
  useEffect(() => {
    accessibleFolderCache
      .fetchFolderChildren(area.rootFolderId)
      .catch(() => {});
  }, [accessibleFolderCache, area.rootFolderId]);

  useEffect(() => {
    if (!teamspaceRoot || (teamspaceRoot.docIds ?? []).length === 0) {
      return;
    }
    accessibleDocCache
      .fetchFolderDocs([...(teamspaceRoot.docIds ?? [])])
      .catch(() => {});
  }, [accessibleDocCache, teamspaceRoot]);

  return (
    <>
      <CollapsibleSection
        navigationEntries={navigationEntries}
        navigationLabel={area.name}
        path={path}
        headerRef={teamspaceRootHeaderDropTargetRef}
        testId="sidebar-teamspace"
        rootDataAttributes={{
          'data-teamspace-root-id': area.rootFolderId,
        }}
        revealOnMount={defaultRenaming}
        titleInteractive
        title={
          canManageArea && renaming ? (
            <NavigationPanelTreeNodeInlineRename
              handleRename={handleAreaRename}
              inputTestId={`rename-area-input-${area.rootFolderId}`}
              protectInitialFocus={protectAreaRenameFocus}
              rawName={area.name}
              setRenaming={handleAreaRenamingChange}
            />
          ) : (
            <span
              onDoubleClick={
                canManageArea
                  ? event => {
                      event.preventDefault();
                      event.stopPropagation();
                      navigationPanelService.setCollapsed(path, false);
                      startAreaRename(false);
                    }
                  : undefined
              }
            >
              {area.name}
            </span>
          )
        }
        actions={
          <SidebarAreaHeaderActions
            create={
              centralCreation
                ? undefined
                : {
                    actions: buildSidebarCreateActions({
                      document: canCreateDocument
                        ? {
                            label:
                              t[
                                'com.streamlain.rootAppSidebar.organize.create.document'
                              ](),
                            onSelect: handleCreateDocumentImmediately,
                          }
                        : undefined,
                      folder: canCreateFolder
                        ? {
                            label:
                              t[
                                'com.streamlain.rootAppSidebar.organize.create.folder'
                              ](),
                            onSelect: handleCreateFolderImmediately,
                          }
                        : undefined,
                    }),
                    testId: 'navigation-panel-teamspace-create-button',
                    tooltip:
                      t[
                        'com.streamlain.rootAppSidebar.organize.create.tooltip'
                      ](),
                  }
            }
            options={{
              rename: canManageArea ? () => startAreaRename(true) : undefined,
              managePermissions: canManageUsers
                ? () => setShareModalOpen(true)
                : undefined,
              trash: canManageArea ? handleTrashArea : undefined,
              reorderActions,
              sortMode,
              onSortModeChange: setSortMode,
              testId: 'navigation-panel-teamspace-options-button',
            }}
          />
        }
      >
        <div
          ref={teamspaceRootContentDropTargetRef}
          className={rootDropZone}
          data-teamspace-root-id={area.rootFolderId}
          data-teamspace-root-present={teamspaceRoot !== null}
          data-accessible-folder-count={accessibleFolders.length}
          data-child-folder-count={childFolders.length}
        >
          <NavigationPanelSidebarSortModeProvider mode={sortMode}>
            <NavigationPanelTreeRoot
              placeholder={
                orderedItems.length === 0 ? (
                  <SidebarCreatePlaceholder
                    icon={<FolderIcon />}
                    message={t[
                      'com.streamlain.rootAppSidebar.organize.empty-folder'
                    ]()}
                    actionText={
                      canCreateFolder
                        ? t[
                            'com.streamlain.rootAppSidebar.organize.empty.new-folders-button'
                          ]()
                        : undefined
                    }
                    onActionClick={
                      canCreateFolder
                        ? handleCreateFolderImmediately
                        : undefined
                    }
                  />
                ) : null
              }
            >
              {orderedItems.map(item =>
                item.type === 'doc' ? (
                  <NavigationPanelDocNode
                    key={item.key}
                    docId={item.docId}
                    hideLinkedChildren
                    onDrop={data => handleRootDocDrop(data, item.docId)}
                    dropEffect={organizeChildrenDropEffect}
                    canDrop={handleRootChildrenCanDrop}
                    location={{
                      at: 'navigation-panel:organize:root-doc',
                      nodeId: item.docId,
                    }}
                    parentPath={path}
                  />
                ) : item.folder.systemKind ===
                  TEAM_MAILBOXES_FOLDER_SYSTEM_KIND ? (
                  <MailFolderNode
                    key={item.key}
                    folder={item.folder}
                    docIds={(item.folder.docIds ?? []).filter(
                      docId => !hiddenMailFolderDocIds.has(docId)
                    )}
                    docTitleById={docTitleById}
                    parentPath={path}
                    label={t[
                      'com.streamlain.rootAppSidebar.privateFolder.mail'
                    ]()}
                    locked={false}
                    testId="sidebar-teamspace-mail-folder"
                  />
                ) : (
                  <NavigationPanelFolderNode
                    key={item.key}
                    nodeId={item.folder.id}
                    defaultRenaming={item.folder.id === newFolderId}
                    onRenamingChange={renaming => {
                      if (!renaming && item.folder.id === newFolderId) {
                        setNewFolderId(null);
                      }
                    }}
                    onDrop={data => handleRootFolderDrop(data, item.folder.id)}
                    dropEffect={organizeChildrenDropEffect}
                    canDrop={handleRootChildrenCanDrop}
                    folderPermissionVariant="teamspace-folder"
                    location={{
                      at: 'navigation-panel:organize:folder-node',
                      nodeId: item.folder.id,
                    }}
                    parentPath={path}
                    hiddenDocIds={hiddenMailFolderDocIds}
                  />
                )
              )}
            </NavigationPanelTreeRoot>
          </NavigationPanelSidebarSortModeProvider>
        </div>
      </CollapsibleSection>
      {shareModalOpen ? (
        <FolderShareModal
          folderId={area.rootFolderId}
          variant="teamspace-root"
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
        />
      ) : null}
    </>
  );
};

type WorkspaceSidebarNavigationItem = WorkspaceNavigationItemModel;

const compareWorkspaceSidebarNavigationItems = (
  a: WorkspaceSidebarNavigationItem,
  b: WorkspaceSidebarNavigationItem
) => compareSidebarManualOrderItems(a, b);

/** What a click on the entry of the area on screen does to that area's sidebar. */
const sidebarToggleTooltip = (
  t: ReturnType<typeof useI18n>,
  open: boolean
) => ({
  content: open
    ? t['com.streamlain.cmdk.affine.left-sidebar.collapse']()
    : t['com.streamlain.cmdk.affine.left-sidebar.expand'](),
  shortcut: AFFINE_LEFT_SIDEBAR_SHORTCUT,
});

export const WorkspaceNavigationItems = ({
  includeKeys,
  labelOverrides,
  reorderable = true,
  showTooltips = false,
  storageKey,
}: {
  includeKeys?: string[];
  labelOverrides?: Partial<Record<'marketplace' | 'settings', string>>;
  reorderable?: boolean;
  showTooltips?: boolean;
  storageKey: string;
}) => {
  const t = useI18n();
  const areaPane = useLiveData(areaSidebar$);
  const workspace = useService(WorkspaceService).workspace;
  const workbench = useService(WorkbenchService).workbench;
  const activeView = useLiveData(workbench.activeView$);
  const localView = useServiceOptional(ViewService)?.view;
  const scope = useMemo(
    () => ({
      workspaceId: workspace.id,
      viewId: localView?.id ?? activeView.id,
    }),
    [workspace.id, localView?.id, activeView.id]
  );
  const workspaceNavigationItems = useWorkspaceNavigationItems(scope).filter(
    item => item.key !== 'trash' && item.key !== 'quick-search'
  );
  const {
    orderedItems: orderedWorkspaceNavigationItems,
    placeItem: placeWorkspaceNavigationItem,
  } = useSidebarManualOrder(
    storageKey,
    workspaceNavigationItems,
    compareWorkspaceSidebarNavigationItems,
    { defaultSortMode: 'manual', manualBase: 'source' }
  );
  const visibleWorkspaceNavigationItems = includeKeys
    ? includeKeys.flatMap(key => {
        const item = orderedWorkspaceNavigationItems.find(
          candidate => candidate.key === key
        );
        return item ? [item] : [];
      })
    : orderedWorkspaceNavigationItems;

  return (
    <>
      {visibleWorkspaceNavigationItems.map((item, index) => {
        const moveUp = () => {
          const before = visibleWorkspaceNavigationItems[index - 1];
          if (before) {
            placeWorkspaceNavigationItem(item.key, {
              at: 'before',
              key: before.key,
            });
          }
        };
        const moveDown = () => {
          const after = visibleWorkspaceNavigationItems[index + 1];
          if (after) {
            placeWorkspaceNavigationItem(item.key, {
              at: 'after',
              key: after.key,
            });
          }
        };
        const label =
          labelOverrides?.[item.key as keyof typeof labelOverrides] ??
          item.label;
        const renderedItem = item.render(label);

        if (!isValidElement(renderedItem)) {
          return null;
        }

        if (!reorderable) {
          return (
            <Tooltip
              {...(areaPane && item.togglesAreaSidebar && item.active()
                ? sidebarToggleTooltip(t, areaPane.open)
                : { content: showTooltips ? label : undefined })}
              key={item.key}
              side="right"
            >
              <div className={railStyles.navigationTooltipTarget}>
                {renderedItem}
              </div>
            </Tooltip>
          );
        }

        return (
          <SidebarReorderableItem
            canMoveDown={index < visibleWorkspaceNavigationItems.length - 1}
            canMoveUp={index > 0}
            itemKey={item.key}
            key={item.key}
            label={label}
            onMoveDown={moveDown}
            onMoveUp={moveUp}
            onPlace={placeWorkspaceNavigationItem}
            scope="root-sidebar:workspace-navigation"
            testIdPrefix="workspace-navigation"
          >
            {renderedItem}
          </SidebarReorderableItem>
        );
      })}
    </>
  );
};

type OtherSidebarNavigationItem = {
  key: string;
  label: string;
  render: () => ReactNode;
};

const compareOtherSidebarNavigationItems = (
  a: OtherSidebarNavigationItem,
  b: OtherSidebarNavigationItem
) => compareSidebarManualOrderItems(a, b);

const OthersNavigationItems = ({
  canImport,
  includeKeys,
  labelOverrides,
  onOpenImportModal,
  reorderable = true,
  showTooltips = false,
  storageKey,
  trashCount,
}: {
  canImport: boolean;
  includeKeys?: string[];
  labelOverrides?: Partial<Record<string, string>>;
  onOpenImportModal: () => void;
  reorderable?: boolean;
  showTooltips?: boolean;
  storageKey: string;
  trashCount: number;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const activeView = useLiveData(
    useService(WorkbenchService).workbench.activeView$
  );
  const localView = useServiceOptional(ViewService)?.view;
  const scope = useMemo(
    () => ({
      workspaceId: workspace.id,
      viewId: localView?.id ?? activeView.id,
    }),
    [workspace.id, localView?.id, activeView.id]
  );
  const navigationItems = useWorkspaceNavigationItems(scope);
  const trash = navigationItems.find(item => item.key === 'trash');
  const canManageUsers = useGuard('Workspace_Users_Manage');
  const canInviteMembers =
    workspace.flavour !== 'local' && canManageUsers === true;
  const otherNavigationItems = useMemo<OtherSidebarNavigationItem[]>(
    () => [
      {
        key: 'trash',
        label: t['com.streamlain.workspaceSubPath.trash'](),
        render: () =>
          trash?.render(undefined, <SidebarBadge count={trashCount} />) ?? null,
      },
      {
        key: 'help',
        label: t['com.streamlain.help.menu'](),
        render: () => <HelpButton />,
      },
      ...(canImport
        ? [
            {
              key: 'import',
              label: t['Import'](),
              render: () => (
                <MenuItem
                  data-testid="slider-bar-import-button"
                  icon={<ImportIcon />}
                  onClick={onOpenImportModal}
                >
                  <span data-testid="import-modal-trigger">
                    {t['Import']()}
                  </span>
                </MenuItem>
              ),
            },
          ]
        : []),
      ...(canInviteMembers
        ? [
            {
              key: 'invite-members',
              label:
                labelOverrides?.['invite-members'] ?? t['Invite Members'](),
              render: () => (
                <InviteMembersButton
                  label={labelOverrides?.['invite-members']}
                />
              ),
            },
          ]
        : []),
    ],
    [
      canImport,
      canInviteMembers,
      labelOverrides,
      onOpenImportModal,
      t,
      trashCount,
      trash,
    ]
  );
  const {
    orderedItems: orderedOtherNavigationItems,
    placeItem: placeOtherNavigationItem,
  } = useSidebarManualOrder(
    storageKey,
    otherNavigationItems,
    compareOtherSidebarNavigationItems,
    { defaultSortMode: 'manual', manualBase: 'source' }
  );
  const visibleOtherNavigationItems = includeKeys
    ? includeKeys.flatMap(key => {
        const item = orderedOtherNavigationItems.find(
          candidate => candidate.key === key
        );
        return item ? [item] : [];
      })
    : orderedOtherNavigationItems;

  return (
    <>
      {visibleOtherNavigationItems.map((item, index) => {
        const moveUp = () => {
          const before = visibleOtherNavigationItems[index - 1];
          if (before) {
            placeOtherNavigationItem(item.key, {
              at: 'before',
              key: before.key,
            });
          }
        };
        const moveDown = () => {
          const after = visibleOtherNavigationItems[index + 1];
          if (after) {
            placeOtherNavigationItem(item.key, {
              at: 'after',
              key: after.key,
            });
          }
        };
        const renderedItem = item.render();

        if (!isValidElement(renderedItem)) {
          return null;
        }

        if (!reorderable) {
          return (
            <Tooltip
              content={showTooltips ? item.label : undefined}
              key={item.key}
              side="right"
            >
              <div className={railStyles.navigationTooltipTarget}>
                {renderedItem}
              </div>
            </Tooltip>
          );
        }

        return (
          <SidebarReorderableItem
            canMoveDown={index < visibleOtherNavigationItems.length - 1}
            canMoveUp={index > 0}
            itemKey={item.key}
            key={item.key}
            label={item.label}
            onMoveDown={moveDown}
            onMoveUp={moveUp}
            onPlace={placeOtherNavigationItem}
            scope="root-sidebar:others-navigation"
            testIdPrefix="others-navigation"
          >
            {renderedItem}
          </SidebarReorderableItem>
        );
      })}
    </>
  );
};

type DocumentsSidebarProps = {
  twoPane: boolean;
  overlayRootProps?: DocumentsSidebarContentProps['overlayRootProps'];
};

export const DocumentsSidebar = memo(
  ({ twoPane, overlayRootProps }: DocumentsSidebarProps): ReactElement => {
    const authService = useService(AuthService);
    const navigationPanelService = useService(NavigationPanelService);
    const navigationPanelRevealService = useService(
      NavigationPanelRevealService
    );
    const notificationCountService = useService(NotificationCountService);
    const appSidebarService = useService(AppSidebarService).sidebar;
    const workspace = useService(WorkspaceService).workspace;
    const profile = useWorkspaceInfo(workspace.meta);
    const workbench = useService(WorkbenchService).workbench;
    const accessibleDocCache = useService(AccessibleDocCacheStore);
    const accessibleFolderCache = useService(AccessibleFolderCacheStore);
    const sessionStatus = useLiveData(authService.session.status$);
    const account = useLiveData(authService.session.account$);
    const collectionsEnabled = useCollectionsFeatureEnabled();
    const tagsEnabled = useTagsFeatureEnabled();
    const [personalActions, setPersonalActions] = useState<ReactNode>(null);
    const [teamActions, setTeamActions] = useState<ReactNode>(null);
    const [appActions, setAppActions] = useState<ReactNode>(null);
    const t = useI18n();
    const { isFeatureAllowed } = useFeatureGate();
    const smallScreenMode = useLiveData(appSidebarService.smallScreenMode$);
    const workspaceNotificationCount = useLiveData(
      notificationCountService.totalCount$
    );
    const workspaceMailEnabled =
      !(profile?.membershipKind === 'house_guests') &&
      workspace.flavour !== 'local' &&
      getWorkspaceMailMissingFeature(isFeatureAllowed) === null;
    const workspaceMailUnreadCount = useWorkspaceMailUnreadCount(
      workspaceMailEnabled ? workspace.id : null
    );
    const { appAreaRoot, canLoadAppArea } = useAppAreaSidebarAvailability();
    const appAreaVisible = canLoadAppArea && appAreaRoot !== null;
    const leftSidebar = useLeftSidebarController({
      accountId: account?.id ?? authService.cachedAccountId,
      appAreaVisible,
      collectionsEnabled,
      navigationPanelService,
      sessionStatus,
      smallScreenMode,
      tagsEnabled,
      workspaceId: workspace.id,
      workspaceMailEnabled,
    });
    const sidebarContentRef = useRef<HTMLDivElement | null>(null);
    const {
      personalAreaList: personalAreaListRef,
      personalSectionHeaderDropTarget: personalSectionHeaderDropTargetRef,
      teamAreasGroup: teamAreasGroupRef,
      teamAreasList: teamAreasListRef,
      teamAreasSectionHeaderDropTarget: teamAreasSectionHeaderDropTargetRef,
      workspaceExpandableList: workspaceExpandableListRef,
    } = leftSidebar.refs;
    const workspaceNavigationOrderStorageKey =
      leftSidebar.workspaceNavigationOrderStorageKey;
    const setSidebarSectionExpanded = leftSidebar.setSectionExpanded;
    const getMobileAwareTopGroupClassName = useCallback(
      (expanded: boolean, desktopClassName = sidebarMainSections) =>
        smallScreenMode
          ? expanded
            ? sidebarMobileActiveSectionGroup
            : sidebarMainSections
          : desktopClassName,
      [smallScreenMode]
    );
    const getMobileAwareSectionClassName = useCallback(
      (expanded: boolean, desktopClassName?: string) =>
        smallScreenMode && expanded ? sidebarMiddleSection : desktopClassName,
      [smallScreenMode]
    );
    const getMobileAwareContentClassName = useCallback(
      (expanded: boolean, desktopClassName?: string) =>
        smallScreenMode && expanded
          ? sidebarMiddleSectionContent
          : desktopClassName,
      [smallScreenMode]
    );
    const getMobileAwareFillLayout = useCallback(
      (expanded: boolean) =>
        smallScreenMode && expanded ? 'single-top' : undefined,
      [smallScreenMode]
    );
    const navigationRevealRequest = useLiveData(
      navigationPanelRevealService.request$
    );

    useEffect(() => {
      if (workspace.flavour === 'local') {
        return;
      }
      accessibleFolderCache.prime();
      accessibleDocCache.prime();
    }, [accessibleDocCache, accessibleFolderCache, workspace.flavour]);

    useEffect(() => {
      if (
        !navigationRevealRequest ||
        navigationRevealRequest.workspaceId !== workspace.id
      ) {
        return;
      }

      const sidebarContent = sidebarContentRef.current;
      if (!sidebarContent) {
        return;
      }

      const folderPath = navigationRevealRequest.docId
        ? getSidebarFolderPathForDoc(
            accessibleFolderCache,
            navigationRevealRequest.docId
          )
        : navigationRevealRequest.folderId
          ? getSidebarFolderPathForFolder(
              accessibleFolderCache,
              navigationRevealRequest.folderId
            )
          : null;
      if (folderPath) {
        const rootSection = getRootSidebarSectionForFolderPath(folderPath);
        if (rootSection) {
          setSidebarSectionExpanded(rootSection, true);
        }
        expandSidebarFolderPath(navigationPanelService, folderPath);
      }

      let cancelled = false;
      let animationFrame: number | null = null;
      let timeout: ReturnType<typeof setTimeout> | null = null;
      let mutationObserver: MutationObserver | null = null;
      let completed = false;

      const cancelScheduledWork = () => {
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
          animationFrame = null;
        }
        if (timeout !== null) {
          clearTimeout(timeout);
          timeout = null;
        }
      };

      const completeRevealIfReady = () => {
        if (cancelled || completed) {
          return completed;
        }

        const target = navigationRevealRequest.docId
          ? findSidebarDocElement(sidebarContent, navigationRevealRequest.docId)
          : navigationRevealRequest.folderId
            ? findSidebarFolderElement(
                sidebarContent,
                navigationRevealRequest.folderId
              )
            : null;
        if (!target) {
          return false;
        }
        const renameSurfaceReady =
          !navigationRevealRequest.rename ||
          !navigationRevealRequest.docId ||
          sidebarContent.ownerDocument.querySelector(
            `[data-testid="rename-modal-input-${navigationRevealRequest.docId}"]`
          ) !== null;
        const rootSectionExpanded = isSidebarTargetRootSectionExpanded(target);
        if (
          !canCompleteNavigationPanelReveal({
            renameSurfaceReady,
            rootSectionExpanded,
          })
        ) {
          return false;
        }

        completed = true;
        mutationObserver?.disconnect();
        cancelScheduledWork();
        scrollSidebarElementIntoView(target);
        if (!navigationRevealRequest.openAfterRenameSubmit) {
          navigationPanelRevealService.clear(navigationRevealRequest.id);
        }
        return true;
      };

      const scheduleReveal = (attempt: number) => {
        cancelScheduledWork();
        animationFrame = requestAnimationFrame(() => {
          animationFrame = requestAnimationFrame(() => {
            if (cancelled || completeRevealIfReady()) {
              return;
            }

            if (attempt < 12) {
              timeout = setTimeout(() => scheduleReveal(attempt + 1), 80);
            } else if (!navigationRevealRequest.openAfterRenameSubmit) {
              navigationPanelRevealService.clear(navigationRevealRequest.id);
            }
          });
        });
      };

      mutationObserver = new MutationObserver(() => {
        if (!completeRevealIfReady()) {
          scheduleReveal(0);
        }
      });
      mutationObserver.observe(sidebarContent, {
        attributeFilter: ['data-expanded'],
        attributes: true,
        childList: true,
        subtree: true,
      });
      scheduleReveal(0);

      return () => {
        cancelled = true;
        mutationObserver?.disconnect();
        cancelScheduledWork();
      };
    }, [
      accessibleFolderCache,
      navigationPanelRevealService,
      navigationPanelService,
      navigationRevealRequest,
      setSidebarSectionExpanded,
      workspace.id,
    ]);

    const handleNavigationRevealRenameFinish = useCallback(
      (requestId: number, reason: 'blur' | 'cancel' | 'submit') => {
        const request = navigationPanelRevealService.request$.value;
        if (!request || request.id !== requestId) {
          return;
        }
        navigationPanelRevealService.clear(requestId);
        if (
          reason === 'submit' &&
          request.openAfterRenameSubmit &&
          request.docId
        ) {
          workbench.openDoc(request.docId, { at: 'active' });
        }
      },
      [navigationPanelRevealService, workbench]
    );

    return (
      <DocumentsSidebarContent
        overlayRootProps={overlayRootProps}
        apps={
          appAreaVisible
            ? {
                action: appActions,
                children: (
                  <AppAreaSidebarContent onActionsChange={setAppActions} />
                ),
                scrollProps: {
                  onScrollTopChange: leftSidebar.scroll.apps.onScrollTopChange,
                  scrollRestoreKey: leftSidebar.scroll.apps.scrollRestoreKey,
                  scrollTop: leftSidebar.scroll.apps.scrollTop,
                },
                sectionProps: leftSidebar.sections.apps,
                title: t['com.streamlain.rootAppSidebar.appsSection'](),
              }
            : undefined
        }
        footer={
          <>
            <SidebarAudioPlayer />
            {LazyUpdaterButton ? (
              <Suspense fallback={null}>
                <LazyUpdaterButton />
              </Suspense>
            ) : null}
          </>
        }
        navigationRevealDocId={
          navigationRevealRequest?.workspaceId === workspace.id
            ? navigationRevealRequest.docId
            : undefined
        }
        navigationRevealRename={
          navigationRevealRequest?.workspaceId === workspace.id &&
          navigationRevealRequest.rename
        }
        navigationRevealRequestId={
          navigationRevealRequest?.workspaceId === workspace.id
            ? navigationRevealRequest.id
            : undefined
        }
        onNavigationRevealRenameFinish={handleNavigationRevealRenameFinish}
        personal={{
          action: personalActions,
          children: (
            <PersonalSidebarSections
              onPrivateActionsChange={setPersonalActions}
              collectionsEnabled={collectionsEnabled}
              tagsEnabled={tagsEnabled}
            />
          ),
          fillLayout: getMobileAwareFillLayout(
            leftSidebar.sections.personal.expanded
          ),
          groupClassName: getMobileAwareTopGroupClassName(
            leftSidebar.sections.personal.expanded
          ),
          listRef: personalAreaListRef,
          scrollProps: {
            onScrollTopChange: leftSidebar.scroll.personal.onScrollTopChange,
            scrollRestoreKey: leftSidebar.scroll.personal.scrollRestoreKey,
            scrollSnap: 'proximity',
            scrollTop: leftSidebar.scroll.personal.scrollTop,
            snapScrollTop: leftSidebar.snapPersonalScrollTop,
          },
          sectionClassName: getMobileAwareSectionClassName(
            leftSidebar.sections.personal.expanded
          ),
          sectionContentClassName: getMobileAwareContentClassName(
            leftSidebar.sections.personal.expanded
          ),
          sectionHeaderRef: personalSectionHeaderDropTargetRef,
          sectionProps: leftSidebar.sections.personal,
          title: t['com.streamlain.rootAppSidebar.personal'](),
        }}
        rootRef={sidebarContentRef}
        teamAreas={{
          action: teamActions,
          children: <TeamAreaSections onActionsChange={setTeamActions} />,
          groupRef: teamAreasGroupRef,
          headerHeight: leftSidebar.sections.teamAreas.headerHeight,
          listRef: teamAreasListRef,
          scrollProps: {
            onScrollTopChange: leftSidebar.scroll.teamAreas.onScrollTopChange,
            scrollRestoreKey: leftSidebar.scroll.teamAreas.scrollRestoreKey,
            scrollSnap: 'proximity',
            scrollTop: leftSidebar.scroll.teamAreas.scrollTop,
            snapScrollTop: leftSidebar.snapTeamAreasScrollTop,
          },
          sectionHeaderRef: teamAreasSectionHeaderDropTargetRef,
          sectionProps: leftSidebar.sections.teamAreas,
          title: t['com.streamlain.rootAppSidebar.teamAreas'](),
        }}
        twoPane={twoPane}
        workspace={{
          children: (
            <WorkspaceNavigationItems
              storageKey={workspaceNavigationOrderStorageKey}
            />
          ),
          collapsedSummary: (
            <WorkspaceSectionHeaderSummary
              mailCount={workspaceMailUnreadCount}
              notificationCount={
                sessionStatus === 'authenticated'
                  ? workspaceNotificationCount
                  : 0
              }
            />
          ),
          fillLayout: getMobileAwareFillLayout(
            leftSidebar.sections.workspace.expanded
          ),
          groupClassName: getMobileAwareTopGroupClassName(
            leftSidebar.sections.workspace.expanded
          ),
          listRef: workspaceExpandableListRef,
          scrollProps: {
            onScrollTopChange: leftSidebar.scroll.workspace.onScrollTopChange,
            scrollRestoreKey: leftSidebar.scroll.workspace.scrollRestoreKey,
            scrollSnap: 'proximity',
            scrollTop: leftSidebar.scroll.workspace.scrollTop,
            snapScrollTop: leftSidebar.snapWorkspaceScrollTop,
          },
          sectionClassName: getMobileAwareSectionClassName(
            leftSidebar.sections.workspace.expanded
          ),
          sectionContentClassName: getMobileAwareContentClassName(
            leftSidebar.sections.workspace.expanded
          ),
          sectionProps: leftSidebar.sections.workspace,
          title: t['com.streamlain.rootAppSidebar.home'](),
        }}
      />
    );
  }
);

DocumentsSidebar.displayName = 'memo(DocumentsSidebar)';

/**
 * This is for the whole affine app sidebar.
 * This component wraps the app sidebar in `@affine/component` with logic and data.
 *
 */
export const RootAppSidebar = memo((): ReactElement => {
  const { workbenchService, cMDKQuickSearchService, authService } = useServices(
    {
      WorkbenchService,
      CMDKQuickSearchService,
      AuthService,
    }
  );

  const sessionStatus = useLiveData(authService.session.status$);
  const account = useLiveData(authService.session.account$);
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const tagsEnabled = useTagsFeatureEnabled();
  const t = useI18n();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const workspace = useService(WorkspaceService).workspace;
  const organizeService = useService(OrganizeService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderTreeLoading = useLiveData(organizeService.folderTree.isLoading$);
  const folderCachePopulated = useLiveData(accessibleFolderCache.isPopulated$);
  const folderLocalCacheLoaded = useLiveData(
    accessibleFolderCache.isLocalCacheLoaded$
  );
  const docCachePopulated = useLiveData(accessibleDocCache.isPopulated$);
  const docLocalCacheLoaded = useLiveData(
    accessibleDocCache.isLocalCacheLoaded$
  );
  const { isLocalEntitlementReady } = useFeatureGate();
  const isCloudWorkspace = workspace.flavour !== 'local';
  const folderCacheReady = folderLocalCacheLoaded || folderCachePopulated;
  const docCacheReady = docLocalCacheLoaded || docCachePopulated;
  const appShellReady = isCloudWorkspace
    ? folderCacheReady && docCacheReady && isLocalEntitlementReady
    : !folderTreeLoading;
  useBootSplashReady(appShellReady);
  useEffect(() => {
    if (isCloudWorkspace) {
      accessibleFolderCache.prime();
      accessibleDocCache.prime();
    }
  }, [accessibleDocCache, accessibleFolderCache, isCloudWorkspace]);
  useEffect(() => {
    recordBootEvent('app-shell-readiness-state', {
      docCachePopulated,
      docLocalCacheLoaded,
      folderCachePopulated,
      folderLocalCacheLoaded,
      folderTreeLoading,
      isLocalEntitlementReady,
      ready: appShellReady,
      workspaceType: isCloudWorkspace ? 'cloud' : 'local',
    });
    if (!isCloudWorkspace || appShellReady) return;
    if (!folderCacheReady) {
      setBootBlocker('folder-cache-local');
    } else if (!docCacheReady) {
      setBootBlocker('doc-cache-local');
    } else if (!isLocalEntitlementReady) {
      setBootBlocker('feature-entitlement-local');
    }
  }, [
    appShellReady,
    docCachePopulated,
    docCacheReady,
    docLocalCacheLoaded,
    folderCachePopulated,
    folderCacheReady,
    folderLocalCacheLoaded,
    folderTreeLoading,
    isCloudWorkspace,
    isLocalEntitlementReady,
  ]);
  const trashCount = useSidebarTrashCount();
  const canCreateDoc = useGuard('Workspace_CreateDoc');
  const canWriteBlobs = useGuard('Workspace_Blobs_Write');
  const canImport =
    workspace.flavour === 'local' ||
    (canCreateDoc === true && canWriteBlobs === true);
  const workbench = workbenchService.workbench;
  const workspaceSelectorOpen = useLiveData(workbench.workspaceSelectorOpen$);
  const navigationRevealRequest = useLiveData(
    navigationPanelRevealService.request$
  );
  const sidebarUiStateStorageKey = useMemo(
    () =>
      createRootAppSidebarResolvedUiStateStorageKey(
        account?.id ?? authService.cachedAccountId,
        workspace.id
      ),
    [account?.id, authService.cachedAccountId, workspace.id]
  );
  const workspaceNavigationOrderStorageKey = sidebarUiStateStorageKey
    ? `${sidebarUiStateStorageKey}:workspace-navigation-order`
    : `streamlain:rootAppSidebar.workspaceNavigationOrder:${workspace.id}`;
  const othersNavigationOrderStorageKey = sidebarUiStateStorageKey
    ? `${sidebarUiStateStorageKey}:others-navigation-order`
    : `streamlain:rootAppSidebar.othersNavigationOrder:${workspace.id}`;
  const performanceInputs = {
    accountId: account?.id ?? null,
    canCreateDoc,
    canImport,
    canWriteBlobs,
    collectionsEnabled,
    navigationRevealRequestId: navigationRevealRequest?.id ?? null,
    sessionStatus,
    tagsEnabled,
    workspaceSelectorOpen,
    trashCount,
  };
  const previousPerformanceInputs = useRef(performanceInputs);
  useEffect(() => {
    const changed = Object.keys(performanceInputs).filter(
      key =>
        !Object.is(
          previousPerformanceInputs.current[
            key as keyof typeof performanceInputs
          ],
          performanceInputs[key as keyof typeof performanceInputs]
        )
    );
    if (changed.length === 0) {
      markSidebarPerformanceSignal('root-render:untracked-input');
    } else {
      changed.forEach(key => markSidebarPerformanceSignal(`root-input:${key}`));
    }
    previousPerformanceInputs.current = performanceInputs;
  });

  const onOpenQuickSearchModal = useCallback(() => {
    cMDKQuickSearchService.applySearchQuery(
      {},
      {
        collectionsEnabled,
        tagsEnabled,
      }
    );
  }, [cMDKQuickSearchService, collectionsEnabled, tagsEnabled]);

  const onWorkspaceSelectorOpenChange = useCallback(
    (open: boolean) => {
      workbench.setWorkspaceSelectorOpen(open);
    },
    [workbench]
  );

  const handleOpenDocs = useCallback(
    (result: {
      docIds: string[];
      entryId?: string;
      isWorkspaceFile?: boolean;
    }) => {
      const { docIds, entryId, isWorkspaceFile } = result;
      // If the imported file is a workspace file, open the entry page.
      if (isWorkspaceFile && entryId) {
        workbench.openDoc(entryId);
      } else if (!docIds.length) {
        return;
      }
      // Open all the docs when there are multiple docs imported.
      if (docIds.length > 1) {
        workbench.openAll();
      } else {
        // Otherwise, open the only doc.
        workbench.openDoc(docIds[0]);
      }
    },
    [workbench]
  );

  const onOpenImportModal = useCallback(() => {
    track.$.navigationPanel.importModal.open();
    workspaceDialogService.open('import', undefined, payload => {
      if (!payload) {
        return;
      }
      handleOpenDocs(payload);
    });
  }, [workspaceDialogService, handleOpenDocs]);

  const currentLocation = useLiveData(workbench.location$);
  const currentPathname = currentLocation.pathname;
  const allPageActive = currentPathname === '/all';
  const activeView = useLiveData(workbench.activeView$);
  const searchScope = useMemo(
    () => ({ workspaceId: workspace.id, viewId: activeView.id }),
    [workspace.id, activeView.id]
  );
  const searchItem = useWorkspaceNavigationItems(searchScope).find(
    item => item.definition.capability === 'workspace-search'
  );
  const semanticLeft = useSemanticDomRegion(
    'left-dom',
    activeView.id,
    SHELL_REGION
  );
  const openAll = useCallback(() => workbench.openAll(), [workbench]);
  const narrowViewport = useNavigationRailViewport(TWO_PANE_OVERLAY_QUERY);
  // The sidebar of the area on screen: its entry in the rail shows whether it is open.
  const areaPane = useLiveData(areaSidebar$);
  const {
    hasActiveDocument,
    showDocumentsNavigation,
    navigationOpen,
    documentsOpen,
    documentsPanelRef,
    workspaceButtonRef,
    setDocumentsOpen,
    onDocumentInteract,
    onDocumentsKeyDown,
    onWorkspaceClick,
  } = useWorkspaceNavigation({
    pathname: currentPathname,
    fromBrowser: currentLocation.state === 'fromBrowser',
    viewId: activeView.id,
    workspaceId: workspace.id,
    transient: narrowViewport,
    openAll,
  });
  const documentsPanelProps = useMemo(
    () => ({
      id: 'workspace-document-navigation',
      ref: documentsPanelRef,
      onKeyDown: onDocumentsKeyDown,
      role: 'navigation',
      'aria-label': t['com.streamlain.rootAppSidebar.workspace'](),
      tabIndex: -1,
    }),
    [documentsPanelRef, onDocumentsKeyDown, t]
  );
  const mobileRailOpen = useLiveData(mobileNavigationRailOpen$);
  const mobileRailViewport = useNavigationRailViewport(
    MOBILE_NAVIGATION_RAIL_QUERY
  );
  const tabletRailViewport = useNavigationRailViewport(
    TABLET_NAVIGATION_RAIL_QUERY
  );
  const mobileRailVisible = !mobileRailViewport || mobileRailOpen;
  const workspaceActive = allPageActive || hasActiveDocument;

  // A phone rail closed by any toggle (burger, backdrop, Escape) also closes its overlay.
  useEffect(() => {
    if (mobileRailViewport && !mobileRailOpen) {
      setDocumentsOpen(false);
    }
  }, [mobileRailOpen, mobileRailViewport, setDocumentsOpen]);

  useEffect(() => {
    if (!mobileRailViewport) {
      setMobileNavigationRailOpen(false);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        // The document-overlay controller restores focus without hiding the
        // mobile rail that contains its trigger.
        if (documentsOpen) return;
        setDocumentsOpen(false);
        setMobileNavigationRailOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [documentsOpen, mobileRailViewport, setDocumentsOpen]);

  useEffect(() => {
    if (
      navigationRevealRequest?.workspaceId === workspace.id &&
      (navigationRevealRequest.docId || navigationRevealRequest.folderId) &&
      shouldNavigationPanelRevealOpenSidebar(navigationRevealRequest.source)
    ) {
      requestWorkspacePaneSidebar('workspace', 'open');
      if (hasActiveDocument) {
        setDocumentsOpen(true);
      }
      if (mobileRailViewport) {
        setMobileNavigationRailOpen(true);
      }
    }
  }, [
    hasActiveDocument,
    mobileRailViewport,
    navigationRevealRequest,
    setDocumentsOpen,
    workspace.id,
  ]);

  const mobileRailLabel = mobileRailOpen
    ? t['com.streamlain.cmdk.affine.left-sidebar.collapse']()
    : t['com.streamlain.cmdk.affine.left-sidebar.expand']();

  return (
    <>
      <AppSidebar staticRail>
        <div
          className={railStyles.shell}
          data-mobile-open={mobileRailVisible}
          data-pane-open={areaPane ? areaPane.open : undefined}
          ref={semanticLeft.ref}
          {...semanticLeft.attributes}
        >
          <button
            aria-label={mobileRailLabel}
            className={railStyles.mobileBackdrop}
            data-open={mobileRailOpen}
            onClick={() => setMobileNavigationRailOpen(false)}
            tabIndex={mobileRailOpen ? 0 : -1}
            type="button"
          />
          <div
            className={railStyles.rail}
            data-mobile-open={mobileRailVisible}
            data-testid="global-navigation-rail"
            id="global-navigation-rail"
          >
            <div className={railStyles.workspaceSlot}>
              <WorkspaceNavigator
                showEnableCloudButton
                showSyncStatus
                open={workspaceSelectorOpen}
                onOpenChange={onWorkspaceSelectorOpenChange}
                dense
              />
            </div>
            <div className={railStyles.searchSection}>
              <div className={railStyles.searchSlot}>
                {searchItem && (
                  <UiNavigationItem
                    scope={searchScope}
                    ownerId="workspace-navigation"
                    definition={searchItem.definition}
                  >
                    <QuickSearchInput
                      data-testid="slider-bar-quick-search-button"
                      data-event-props="$.navigationPanel.$.quickSearch"
                      onClick={onOpenQuickSearchModal}
                      showTooltip={tabletRailViewport}
                    />
                  </UiNavigationItem>
                )}
              </div>
              <div className={railStyles.searchDivider} />
            </div>
            <SidebarScrollableContainer
              className={railStyles.scrollRegion}
              contentPadding="none"
              scrollbarVisibility="scroll"
            >
              <div className={railStyles.scrollContent}>
                <WorkspaceNavigationItems
                  includeKeys={['notifications']}
                  reorderable={false}
                  showTooltips={tabletRailViewport}
                  storageKey={workspaceNavigationOrderStorageKey}
                />
                <Tooltip
                  {...(workspaceActive
                    ? sidebarToggleTooltip(t, navigationOpen)
                    : {
                        content: tabletRailViewport
                          ? t['com.streamlain.rootAppSidebar.workspace']()
                          : undefined,
                      })}
                  side="right"
                >
                  <button
                    ref={workspaceButtonRef}
                    aria-controls={
                      hasActiveDocument
                        ? 'workspace-document-navigation'
                        : undefined
                    }
                    aria-expanded={workspaceActive ? navigationOpen : undefined}
                    aria-label={t['com.streamlain.rootAppSidebar.workspace']()}
                    aria-current={allPageActive ? 'page' : undefined}
                    className={railStyles.docsButton}
                    data-active={workspaceActive}
                    data-testid="global-navigation-docs-button"
                    onClick={onWorkspaceClick}
                    type="button"
                  >
                    <TeamWorkspaceIcon />
                    <span>
                      {t['com.streamlain.rootAppSidebar.workspace']()}
                    </span>
                  </button>
                </Tooltip>
                <WorkspaceNavigationItems
                  includeKeys={[
                    'mail',
                    'calendar',
                    'journals',
                    'workflows',
                    'app-bases',
                    'marketplace',
                    'graph-explorer',
                  ]}
                  labelOverrides={{
                    marketplace: t['com.streamlain.rootAppSidebar.hub'](),
                  }}
                  reorderable={false}
                  showTooltips={tabletRailViewport}
                  storageKey={workspaceNavigationOrderStorageKey}
                />
                <div
                  className={railStyles.bottomNavigationGroup}
                  data-testid="global-navigation-extras"
                >
                  <div className={railStyles.partialDivider} />
                  <OthersNavigationItems
                    canImport={canImport}
                    includeKeys={['invite-members', 'import', 'trash', 'help']}
                    labelOverrides={{
                      'invite-members':
                        t['com.streamlain.rootAppSidebar.members'](),
                    }}
                    onOpenImportModal={onOpenImportModal}
                    reorderable={false}
                    showTooltips={tabletRailViewport}
                    storageKey={othersNavigationOrderStorageKey}
                    trashCount={trashCount}
                  />
                </div>
              </div>
            </SidebarScrollableContainer>
            <div className={railStyles.footerSection}>
              <div className={railStyles.footerDivider} />
              <div className={railStyles.footer}>
                <div className={railStyles.avatarSlot}>
                  <div className={railStyles.avatarControl}>
                    <UserInfo />
                  </div>
                  <WorkspaceConnectivityStatus />
                </div>
                <WorkspaceNavigationItems
                  includeKeys={['settings']}
                  labelOverrides={{
                    settings: t['com.streamlain.rootAppSidebar.settings'](),
                  }}
                  reorderable={false}
                  showTooltips={tabletRailViewport}
                  storageKey={workspaceNavigationOrderStorageKey}
                />
              </div>
            </div>
          </div>
          {showDocumentsNavigation && (
            <WorkspaceDocumentNavigation
              allPageActive={allPageActive}
              open={navigationOpen && (allPageActive || mobileRailVisible)}
              narrow={narrowViewport}
              view={activeView}
              onOpenChange={setDocumentsOpen}
              onDocumentInteract={onDocumentInteract}
            >
              <DocumentsSidebarProvider>
                {/* The sidebar itself never renders for being opened or closed. */}
                <NavigationOverlayPaneOpenState
                  mobileOpen={
                    allPageActive ? navigationOpen : mobileRailVisible
                  }
                  open={navigationOpen}
                >
                  <DocumentsSidebar
                    twoPane={allPageActive}
                    overlayRootProps={documentsPanelProps}
                  />
                </NavigationOverlayPaneOpenState>
              </DocumentsSidebarProvider>
            </WorkspaceDocumentNavigation>
          )}
        </div>
      </AppSidebar>
    </>
  );
});

RootAppSidebar.displayName = 'memo(RootAppSidebar)';
